import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { trackViewItem, trackBeginCheckout } from '@/lib/analytics';
import SEO from '@/components/SEO';
import {
  FileText,
  AlertCircle,
  ArrowLeft,
  Book,
  Lock,
  Info,
  ShieldCheck,
  ArrowRight,
  HelpCircle,
  Users,
  UserCheck,
  X,
  Printer,
  CircleCheck,
  CircleDashed,
  CircleAlert,
  Calendar,
  Coins,
  Home,
  Scale,
  Landmark,
  Receipt,
  Download,
  Trash2,
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { DASHBOARD } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NOT_IDENTIFIED = 'Non identifié dans le document';

const PRIORITY_LABEL = {
  haute: 'PRIORITAIRE',
  moyenne: 'À PRÉVOIR',
  basse: 'SANS URGENCE',
};

// ----- Reusable helpers -----

const ConfidenceBadge = ({ confidence, rationale, size = 'sm' }) => {
  if (!confidence) return null;
  const tone =
    confidence === 'élevée'
      ? 'bg-[#F0F4EE] text-[#2D6A4F] border-[#2D6A4F]/25'
      : confidence === 'moyenne' || confidence === 'modérée'
      ? 'bg-[#FBF6E6] text-[#8B6A14] border-[#E9C46A]/40'
      : 'bg-[#FBEAEA] text-[#9B2B2B] border-[#D67A7A]/40';
  const padding = size === 'xs' ? 'px-2 py-0.5 text-[9px]' : 'px-2.5 py-1 text-[10px]';
  const cls = `font-['IBM_Plex_Mono'] uppercase tracking-[0.14em] rounded-full border ${tone} ${padding}`;
  const label = `Confiance ${confidence}`;
  if (!rationale) {
    return (
      <span className={cls} data-testid="confidence-badge">
        {label}
      </span>
    );
  }
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={`${cls} inline-flex items-center gap-1 cursor-help`}
          data-testid="confidence-badge"
        >
          {label}
          <HelpCircle className="h-2.5 w-2.5 opacity-60" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-xs bg-[#1A2530] text-white border-[#1A2530] font-['Work_Sans'] text-[12px] leading-relaxed"
      >
        <p className="font-semibold mb-1">Pourquoi {confidence} ?</p>
        <p className="opacity-90">{rationale}</p>
      </TooltipContent>
    </Tooltip>
  );
};

const SourceTag = ({ page, reference }) => {
  if (!page && !reference) return null;
  const label = page ? `p. ${page}` : reference;
  return (
    <span
      className="font-['IBM_Plex_Mono'] text-[10px] tracking-[0.1em] uppercase text-[#2C363F] bg-[#F9F7F3] border border-[#E2DBCF]/70 px-2 py-0.5 rounded-full inline-flex items-center gap-1"
      title={reference || ''}
      data-testid="source-tag"
    >
      Source · {label}
    </span>
  );
};

const Field = ({ label, value, confidence, confidenceRationale, sourcePage }) => (
  <div className="py-2.5">
    <p className="font-['Work_Sans'] text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-[#2C363F] mb-1">
      {label}
    </p>
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <p
        className={`font-['Work_Sans'] text-[14px] sm:text-[15px] leading-snug ${
          value ? 'text-[#1A2530]' : 'text-[#2C363F] italic opacity-70'
        }`}
      >
        {value || NOT_IDENTIFIED}
      </p>
      {value && (
        <span className="flex flex-wrap gap-1.5">
          <ConfidenceBadge confidence={confidence} rationale={confidenceRationale} size="xs" />
          <SourceTag page={sourcePage} />
        </span>
      )}
    </div>
  </div>
);

const SectionHeader = ({ eyebrow, title, description }) => (
  <div className="mb-5 sm:mb-6">
    <p className="font-['Work_Sans'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#2C363F] mb-2">
      {eyebrow}
    </p>
    <h2 className="font-['Work_Sans'] font-semibold text-xl sm:text-[22px] text-[#1A2530] leading-tight">
      {title}
    </h2>
    {description && (
      <p className="font-['Work_Sans'] text-[13px] sm:text-sm text-[#2C363F] mt-2 leading-relaxed max-w-2xl">
        {description}
      </p>
    )}
  </div>
);

const has = (arr) => Array.isArray(arr) && arr.length > 0;

// ----- Role normalization -----
// Maps the many ways the model writes a role (« Vendeur », « vendeurs »,
// « acquéreur », « héritière », …) to a canonical, accent-free key that
// matches the backend's normalization.
const _stripAccents = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const ROLE_ALIAS = {
  vendeur: 'vendeur', vendeurs: 'vendeur', cedant: 'vendeur',
  acquereur: 'acquereur', acheteur: 'acquereur',
  donataire: 'donataire', donataires: 'donataire',
  donateur: 'donateur', donateurs: 'donateur',
  heritier: 'heritier', heritiere: 'heritier', heritiers: 'heritier',
  legataire: 'legataire',
  usufruitier: 'usufruitier', usufruitiere: 'usufruitier',
  'nu-proprietaire': 'nu-proprietaire',
  'nu proprietaire': 'nu-proprietaire',
  'executeur-testamentaire': 'executeur-testamentaire',
  'executeur testamentaire': 'executeur-testamentaire',
  tous: 'tous', general: 'tous',
};

const normalizeRole = (raw) => {
  const key = _stripAccents(raw);
  if (!key) return null;
  if (ROLE_ALIAS[key]) return ROLE_ALIAS[key];
  // fuzzy: any alias contained in the string
  for (const alias of Object.keys(ROLE_ALIAS)) {
    if (alias && key.includes(alias)) return ROLE_ALIAS[alias];
  }
  return null;
};

// Returns true when an item (action or question) should be shown to the
// currently selected role. Items targeted at `tous` are always visible.
const matchesSelectedRole = (item, selectedRoleCanonical) => {
  if (!selectedRoleCanonical) return true;
  const target = normalizeRole(item?.target_role);
  if (!target || target === 'tous') return true;
  return target === selectedRoleCanonical;
};

// Party-matching utilities (no auth — purely client-side)
const normalize = (s) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const partyMentioned = (text, partyName) => {
  if (!text || !partyName) return false;
  const t = normalize(text);
  const p = normalize(partyName);
  if (!p) return false;
  if (t.includes(p)) return true;
  // Match by "first last" tokens — handles "M. Adrien GADET" vs "Adrien GADET"
  const tokens = p.split(/\s+/).filter((tok) => tok.length >= 2);
  return tokens.length >= 2 && tokens.every((tok) => t.includes(tok));
};

import {
  parseFrenchMoney,
  displayAmount,
  isMeaningfulAmount,
  isAnomalouslyLow,
} from '@/lib/money';

// Extract first French monetary amount from text — handles all narrow
// no-break spaces AND comma decimals. Returns the canonical re-formatted
// string ("330 725,20 €") parsed back from the actual Number, OR the raw
// input verbatim if it does not parse. NEVER returns a lossy substring.
const extractAmount = (raw) => displayAmount(raw);

// Parse a French amount string into a JS number.
const parseAmountToNumber = (raw) => parseFrenchMoney(raw);

// Map a monetary amount to a discreet bracket (notarial tone, no exact figure).
const valueBracket = (n) => {
  if (n === null || !Number.isFinite(n)) return null;
  if (n < 50000) return 'Inférieure à 50 000 €';
  if (n < 100000) return 'Entre 50 000 € et 100 000 €';
  if (n < 250000) return 'Entre 100 000 € et 250 000 €';
  if (n < 500000) return 'Entre 250 000 € et 500 000 €';
  if (n < 1000000) return 'Entre 500 000 € et 1 M€';
  if (n < 2000000) return 'Entre 1 M€ et 2 M€';
  return 'Supérieure à 2 M€';
};

// Categorize an asset based on its name into a discreet, premium label.
const categorizeAssetName = (name) => {
  if (!name) return null;
  const n = name.toLowerCase();
  if (/(appartement|studio|maison|villa|loft|duplex|immeuble|résidence)/i.test(n)) return 'Bien immobilier résidentiel';
  if (/(terrain|parcelle|hectare)/i.test(n)) return 'Terrain';
  if (/(garage|parking|cave|box)/i.test(n)) return 'Bien immobilier accessoire';
  if (/(local|bureau|commerce|fonds de commerce|boutique)/i.test(n)) return 'Bien immobilier professionnel';
  if (/(portefeuille|titre|action|obligation|sicav|opcvm|compte titres|pea)/i.test(n)) return 'Placement financier';
  if (/(assurance.?vie|contrat d['’]assurance)/i.test(n)) return 'Assurance-vie';
  if (/(compte|livret|espèce|liquidité)/i.test(n)) return 'Avoirs bancaires';
  return 'Actif identifié';
};

// Detect whether `immediate_action` actually means "no action"
const NO_ACTION_RE = /(aucune?\s*action|aucune?\s*démarche|rien\s+à\s+faire|pas\s+d['']action|non\s+identifi)/i;

const ACTION_STATUS_MAP = {
  'aucune action requise': { label: 'Aucune action requise', tone: 'calm' },
  'action recommandée': { label: 'Action recommandée', tone: 'warn' },
  'action à prévoir': { label: 'Action à prévoir', tone: 'warn' },
  'action urgente': { label: 'Action urgente', tone: 'urgent' },
};

const deriveActionStatus = (immediateAction, explicitStatus = null) => {
  // Prefer the explicit 4-level status returned by the LLM when present.
  if (explicitStatus) {
    const key = String(explicitStatus).trim().toLowerCase();
    const mapped = ACTION_STATUS_MAP[key];
    if (mapped) {
      return {
        label: mapped.label,
        tone: mapped.tone,
        actor: null,
        detail: immediateAction || null,
      };
    }
  }
  if (!immediateAction || NO_ACTION_RE.test(immediateAction)) {
    return {
      label: 'Aucune action requise',
      tone: 'calm',
      actor: null,
      detail: immediateAction || null,
    };
  }
  const t = normalize(immediateAction);
  let actor = 'Vous';
  if (/notaire/.test(t)) actor = 'Le notaire';
  else if (/acqu[eé]reur|acheteur/.test(t)) actor = "L'acquéreur";
  else if (/h[eé]ritier|coh[eé]ritier|donataire/.test(t)) actor = 'Un autre héritier';
  const urgent = /(immédiat|imm[eé]diat|urgent|requis|obligatoire|sans délai)/i.test(immediateAction);
  return {
    label: urgent ? 'Action urgente' : 'Action à prévoir',
    tone: urgent ? 'urgent' : 'warn',
    actor,
    detail: immediateAction,
  };
};

const Dimmable = ({ dimmed, children, className = '' }) => (
  <div
    className={`transition-opacity duration-300 ${dimmed ? 'opacity-40' : 'opacity-100'} ${className}`}
  >
    {children}
  </div>
);

// ===== FREEMIUM helpers =====
// NOTE: any client-side admin / "demo unlock" toggle was deliberately
// removed. The only path that flips a dossier to premium is the Stripe
// checkout flow (`POST /api/payments/checkout/{document_id}` ➜ Stripe
// hosted page ➜ `/api/payments/status/{session_id}` confirms the
// transaction server-side). The legacy `?admin=1` / `localStorage
// 'herita_admin'` shortcut would have allowed any visitor to bypass
// the paywall in the browser and was deleted in the v12 security pass.

const LockedSection = ({ locked, children, innerClassName = '', focusReorder = false }) => {
  if (!locked) {
    // When unlocked, wrap children in a flex container so that
    // `style={{ order }}` on individual sections re-orders them in focus
    // mode without changing the JSX source order.
    if (focusReorder) {
      return <div className="flex flex-col gap-8">{children}</div>;
    }
    return children;
  }
  return (
    <div className="relative" data-testid="locked-section">
      {/* Subtle, non-sticky lock indicator — communicates state without
          aggressive overlay. The real CTA lives above this block. */}
      <div className="flex items-center justify-center mb-4">
        <span
          className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#2C363F] opacity-70 inline-flex items-center gap-1.5"
          data-testid="locked-indicator"
        >
          <Lock className="h-3 w-3" />
          Contenu verrouillé
        </span>
      </div>
      <div
        aria-hidden="true"
        className={`pointer-events-none select-none transition-all duration-500 ${innerClassName}`}
        style={{ filter: 'blur(4px)', opacity: 0.55 }}
      >
        {children}
      </div>
    </div>
  );
};

// ===== Premium CTA card — the real conversion surface =====
const PremiumCTACard = ({ onUnlock }) => (
  <Card
    className="bg-white rounded-2xl p-7 sm:p-9 border border-[#1A2530]/15 shadow-sm relative overflow-hidden"
    data-testid="premium-cta-card"
  >
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-full bg-[#1A2530] flex items-center justify-center flex-shrink-0">
        <Lock className="h-5 w-5 text-[#E3D5CA]" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#2C363F] mb-2">
          Lecture personnalisée
        </p>
        <h2 className="font-['Cormorant_Garamond'] font-medium text-2xl sm:text-3xl text-[#1A2530] leading-tight">
          Débloquer ma lecture personnalisée
        </h2>
        <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#2C363F] leading-relaxed mt-3 max-w-2xl">
          Comprendre ce que ce document signifie pour vous.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={onUnlock}
            className="font-['Work_Sans'] text-[14px] font-semibold bg-[#1A2530] hover:bg-[#2C363F] text-white rounded-full px-5 py-2.5 transition-colors inline-flex items-center gap-2"
            data-testid="premium-cta-button"
          >
            <ShieldCheck className="h-4 w-4" />
            Débloquer ma lecture personnalisée — 19&nbsp;€
          </button>
          <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] opacity-70">
            Paiement unique · sans abonnement
          </p>
        </div>
      </div>
    </div>
  </Card>
);

const PremiumTeaserCard = ({ selectedParty, hints, onUnlock, onChange }) => (
  <Card
    className="bg-white rounded-2xl p-8 sm:p-12 border border-[#1A2530]/15 shadow-md relative overflow-hidden"
    data-testid="premium-teaser"
  >
    {/* Decorative top stripe — preserves premium feel */}
    <div
      className="absolute top-0 left-0 right-0 h-1"
      style={{ background: 'linear-gradient(90deg, #1A2530 0%, #E3D5CA 100%)' }}
    />

    {/* Header — editorial */}
    <div className="text-center mb-8 sm:mb-10">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#1A2530] mb-5">
        <UserCheck className="h-5 w-5 text-[#E3D5CA]" />
      </div>
      <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#2C363F] mb-3">
        Lecture personnelle de {selectedParty}
      </p>
      <h2 className="font-['Cormorant_Garamond'] font-medium text-[32px] sm:text-[42px] text-[#1A2530] leading-[1.1] mb-4">
        Votre situation personnelle a été identifiée
      </h2>
      <p className="font-['Work_Sans'] text-[15px] sm:text-[16px] text-[#2C363F] leading-relaxed max-w-xl mx-auto">
        Hérita a détecté plusieurs éléments qui vous concernent directement dans ce document.
      </p>
    </div>

    {/* High-level findings — neutral, no specifics revealed */}
    <ul
      className="space-y-3 mb-10 max-w-xl mx-auto"
      data-testid="premium-teaser-hints"
    >
      {hints.map((h, idx) => (
        <li
          key={idx}
          className="flex items-center gap-4 p-4 rounded-xl bg-[#F9F7F3] border border-[#E2DBCF]"
          data-testid={`teaser-hint-${idx}`}
        >
          <div className="w-9 h-9 rounded-full bg-white border border-[#E2DBCF] flex items-center justify-center flex-shrink-0">
            <Lock className="h-3.5 w-3.5 text-[#1A2530]" />
          </div>
          <span className="font-['Work_Sans'] text-[15px] sm:text-[16px] text-[#1A2530] leading-snug">
            {h}
          </span>
        </li>
      ))}
    </ul>

    {/* Primary conversion CTA */}
    <div className="p-6 sm:p-8 rounded-2xl bg-[#1A2530] text-white max-w-xl mx-auto">
      <div className="flex items-center gap-2 mb-2">
        <Lock className="h-3.5 w-3.5 text-[#E3D5CA]" />
        <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#E3D5CA] opacity-80">
          Lecture personnalisée
        </p>
      </div>
      <p className="font-['Cormorant_Garamond'] font-medium text-[22px] sm:text-[26px] leading-tight mb-4">
        En moins de 2 minutes, comprenez exactement ce que cet acte implique pour vous.
      </p>
      <button
        type="button"
        onClick={onUnlock}
        className="w-full sm:w-auto font-['Work_Sans'] text-[14px] font-semibold bg-[#E3D5CA] hover:bg-white text-[#1A2530] rounded-full px-6 py-3 transition-colors inline-flex items-center justify-center gap-2"
        data-testid="unlock-premium-cta"
      >
        <ShieldCheck className="h-4 w-4" />
        Débloquer ma lecture personnalisée — 19&nbsp;€
      </button>
      <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.18em] text-[#E3D5CA] opacity-70 mt-3">
        Paiement unique • Sans abonnement
      </p>
      {onChange && (
        <button
          type="button"
          onClick={onChange}
          className="font-['Work_Sans'] text-[12px] text-[#E3D5CA] hover:text-white underline underline-offset-2 mt-5"
          data-testid="teaser-change-party"
        >
          Ce n'est pas moi
        </button>
      )}
    </div>
  </Card>
);

const FreeCountsBar = ({ analysis }) => {
  const parties = (analysis.parties || []).length;
  const beneficiaries = (() => {
    const set = new Set();
    (analysis.assets || []).forEach((a) => {
      (a.beneficiaries || []).forEach((b) => b?.name && set.add(b.name));
      if (a.beneficiary) set.add(a.beneficiary);
    });
    return set.size;
  })();
  const assets = (analysis.assets || []).length;
  const totalRaw = analysis.estate_summary?.total_declared_value || null;
  const totalNum = parseAmountToNumber(totalRaw);
  const total =
    totalRaw && (totalNum === null || totalNum > 0) ? totalRaw : null;

  const items = [
    { icon: <Users className="h-4 w-4" />, value: parties, label: parties > 1 ? 'personnes détectées' : 'personne détectée' },
    { icon: <UserCheck className="h-4 w-4" />, value: beneficiaries, label: beneficiaries > 1 ? 'bénéficiaires' : 'bénéficiaire' },
    { icon: <FileText className="h-4 w-4" />, value: assets, label: assets > 1 ? 'biens identifiés' : 'bien identifié' },
  ];
  if (total) {
    items.push({ icon: <Coins className="h-4 w-4" />, value: total, label: 'valeur totale détectée', big: true });
  }

  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4"
      data-testid="free-counts-bar"
    >
      {items.map((it, idx) => (
        <div
          key={idx}
          className="p-4 sm:p-5 rounded-2xl bg-white border border-[#E2DBCF]"
          data-testid={`count-${idx}`}
        >
          <div className="w-8 h-8 rounded-full bg-[#E3D5CA]/60 flex items-center justify-center text-[#1A2530] mb-3">
            {it.icon}
          </div>
          <p
            className={`font-['Cormorant_Garamond'] font-semibold text-[#1A2530] leading-none ${
              it.big ? 'text-[22px] sm:text-[26px] break-words' : 'text-[28px] sm:text-[34px]'
            }`}
          >
            {it.value}
          </p>
          <p className="font-['Work_Sans'] text-[11px] uppercase tracking-[0.16em] text-[#2C363F] mt-2 leading-snug">
            {it.label}
          </p>
        </div>
      ))}
    </div>
  );
};

// ===== Dossier Hérita — INLINE HTML view (no PDF, no blob, no popup) =====
// Renders the same content as the server-side ReportLab PDF, but in pure React
// inside a modal. Guaranteed to work in any iframe / ad-blocker context.

const DossierEyebrow = ({ children }) => (
  <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#2C363F] opacity-80 mb-2">
    {children}
  </p>
);

const DossierSection = ({ eyebrow, title, children }) => (
  <section className="mb-10">
    {eyebrow && <DossierEyebrow>{eyebrow}</DossierEyebrow>}
    {title && (
      <h2 className="font-['Cormorant_Garamond'] font-medium text-2xl sm:text-3xl text-[#1A2530] mb-4 leading-tight">
        {title}
      </h2>
    )}
    {children}
  </section>
);

const DossierInline = ({ analysis, selectedParty }) => {
  if (!analysis) return null;
  // v8.1 — STRICT: no auto-selection of the first detected party. If the user
  // hasn't explicitly picked a profile card, the dossier opens in neutral /
  // global mode (document-centric, not person-centric).
  const personal = selectedParty
    ? (analysis.personal_relevance || []).find((p) => p.party_name === selectedParty)
    : null;
  const neutralMode = !selectedParty;
  const es = analysis.estate_summary || null;

  return (
    <div className="max-w-3xl mx-auto" data-testid="dossier-inline">
      {/* Cover */}
      <DossierSection>
        <DossierEyebrow>Dossier Hérita</DossierEyebrow>
        <h1 className="font-['Cormorant_Garamond'] font-normal text-4xl sm:text-5xl text-[#1A2530] leading-[1.05] mb-4">
          {neutralMode ? 'Vue d\'ensemble' : 'Lecture personnalisée'}
        </h1>
        <div className="space-y-1.5">
          <p className="font-['Work_Sans'] text-[14px] text-[#1A2530]">
            <span className="opacity-70">Document analysé · </span>
            <span className="font-semibold">{analysis.document_filename || 'document'}</span>
          </p>
          {selectedParty && (
            <p className="font-['Work_Sans'] text-[14px] text-[#1A2530]">
              <span className="opacity-70">Préparé pour · </span>
              <span className="font-semibold">{selectedParty}</span>
            </p>
          )}
          {analysis.document_category && (
            <p className="font-['Work_Sans'] text-[14px] text-[#1A2530]">
              <span className="opacity-70">Type identifié · </span>
              <span className="font-semibold">{analysis.document_category}</span>
              {analysis.document_category_confidence && (
                <span className="opacity-70 italic"> · confiance {analysis.document_category_confidence}</span>
              )}
            </p>
          )}
          {analysis.document_date && (
            <p className="font-['Work_Sans'] text-[14px] text-[#1A2530]">
              <span className="opacity-70">Date de l'acte · </span>{analysis.document_date}
            </p>
          )}
          {(analysis.notary_name || analysis.notary_office) && (
            <p className="font-['Work_Sans'] text-[14px] text-[#1A2530]">
              <span className="opacity-70">Notaire · </span>
              {analysis.notary_name || '—'}
              {analysis.notary_office && <span className="opacity-70"> · {analysis.notary_office}</span>}
            </p>
          )}
          {analysis.document_reference && (
            <p className="font-['Work_Sans'] text-[14px] text-[#1A2530]">
              <span className="opacity-70">Référence · </span>{analysis.document_reference}
            </p>
          )}
        </div>
        <p className="font-['Work_Sans'] text-[12px] text-[#2C363F] italic mt-6 pt-5 border-t border-[#E2DBCF] leading-relaxed">
          Ce dossier reprend, point par point, ce que le document semble organiser.
          Hérita reste un outil de lecture ; votre notaire demeure votre référence
          pour toute décision juridique.
        </p>
      </DossierSection>

      {/* Neutral-mode prompt — invites profile selection when no party picked */}
      {neutralMode && has(analysis.personal_relevance) && (
        <DossierSection>
          <div
            className="p-5 rounded-xl bg-[#F0F4EE] border border-[#2D6A4F]/25"
            data-testid="dossier-neutral-prompt"
          >
            <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#2D6A4F] mb-2 opacity-90">
              Lecture personnalisée
            </p>
            <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#1A2530] leading-relaxed">
              Choisissez la personne que vous êtes pour accéder à votre lecture personnalisée.
              Le dossier reste consultable dans sa version générale ci-dessous.
            </p>
          </div>
        </DossierSection>
      )}

      {/* v9 — Succession-specific overview, only inside the neutral view. */}
      {neutralMode && (
        <DossierSection>
          <SuccessionOverviewCard analysis={analysis} />
        </DossierSection>
      )}

      {/* Synthesis */}
      {(analysis.act_synthesis || analysis.document_summary) && (
        <DossierSection eyebrow="En 30 secondes" title="Ce que cet acte fait, concrètement">
          {analysis.act_synthesis && (
            <p className="font-['Work_Sans'] text-[15px] text-[#1A2530] leading-relaxed whitespace-pre-line">
              {analysis.act_synthesis}
            </p>
          )}
          {analysis.document_summary && analysis.document_summary !== analysis.act_synthesis && (
            <p className="font-['Work_Sans'] text-[14px] text-[#2C363F] leading-relaxed italic mt-4">
              Lecture générale — {analysis.document_summary}
            </p>
          )}
        </DossierSection>
      )}

      {/* Personal */}
      {personal && (
        <DossierSection eyebrow="Ce que vous recevez" title={personal.share_label || 'Droit reçu'}>
          {personal.what_received && personal.what_received !== personal.share_label && (
            <p className="font-['Work_Sans'] text-[14px] text-[#1A2530] leading-relaxed mb-3">
              {personal.what_received}
            </p>
          )}
          {personal.legal_mechanism && (
            <p className="font-['Work_Sans'] text-[14px] text-[#1A2530] mb-1.5">
              <span className="opacity-70">Mécanisme juridique · </span>
              <span className="font-semibold">{personal.legal_mechanism}</span>
            </p>
          )}
          {personal.legal_mechanism_explanation && (
            <p className="font-['Work_Sans'] text-[14px] text-[#2C363F] leading-relaxed mb-3">
              {personal.legal_mechanism_explanation}
            </p>
          )}
          {personal.value_received && (
            <p className="font-['Work_Sans'] text-[14px] text-[#1A2530] mb-1.5">
              <span className="opacity-70">Valeur fiscale identifiée · </span>
              <span className="font-semibold">{personal.value_received}</span>
            </p>
          )}
          {personal.taxes_due && (
            <p className="font-['Work_Sans'] text-[14px] text-[#1A2530] mb-3">
              <span className="opacity-70">Fiscalité · </span>
              {personal.taxes_due}
            </p>
          )}
          {has(personal.calculation_breakdown) && (
            <div className="mt-4 p-4 rounded-xl bg-white border border-[#E2DBCF]">
              <p className="font-['Work_Sans'] text-[12px] uppercase tracking-[0.18em] text-[#2C363F] mb-2 opacity-80">
                Détail du calcul fiscal
              </p>
              <table className="w-full text-[13px]">
                <tbody>
                  {personal.calculation_breakdown.map((step, idx) => (
                    <tr key={idx} className="border-b border-[#E2DBCF]/40 last:border-b-0">
                      <td className="py-2 font-['Work_Sans'] text-[#2C363F] pr-4">{step.label}</td>
                      <td className="py-2 font-['IBM_Plex_Mono'] text-right text-[#1A2530]">{step.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {has(personal.consequences_plain) && (
            <div className="mt-4 p-4 rounded-xl bg-[#F0F4EE] border border-[#2D6A4F]/20">
              <p className="font-['Work_Sans'] text-[12px] uppercase tracking-[0.18em] text-[#2D6A4F] mb-2">
                Ce que cela veut dire pour vous
              </p>
              <ul className="space-y-1.5">
                {personal.consequences_plain.map((c, idx) => (
                  <li key={idx} className="font-['Work_Sans'] text-[14px] text-[#1A2530] leading-snug">
                    — {c}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </DossierSection>
      )}

      {/* Assets */}
      {has(analysis.assets) && (
        <DossierSection eyebrow="Biens identifiés" title="Les biens objet de l'acte">
          <ul className="space-y-4">
            {analysis.assets.map((a, idx) => (
              <li key={idx} className="p-4 rounded-xl bg-white border border-[#E2DBCF]">
                <p className="font-['Work_Sans'] font-semibold text-[15px] text-[#1A2530] leading-snug">
                  {a.name}
                </p>
                {a.address && (
                  <p className="font-['Work_Sans'] text-[13px] text-[#2C363F] italic mt-0.5">
                    {a.address}
                  </p>
                )}
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-[#1A2530]">
                  {a.value && (
                    <span>
                      <span className="opacity-70">Valeur · </span>
                      <span className="font-semibold">{a.value}</span>
                      {a.value_context && <span className="opacity-70 italic"> ({a.value_context})</span>}
                    </span>
                  )}
                  {a.rights_transferred && (
                    <span>
                      <span className="opacity-70">Droits transmis · </span>
                      {a.rights_transferred}
                    </span>
                  )}
                </div>
                {has(a.beneficiaries) && (
                  <ul className="mt-2 space-y-0.5">
                    {a.beneficiaries.map((b, j) => (
                      <li key={j} className="font-['Work_Sans'] text-[13px] text-[#1A2530]">
                        → {b.name} · {b.right || '—'} · {b.share || '—'}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </DossierSection>
      )}

      {/* Estate summary */}
      {es && (
        <DossierSection eyebrow="Synthèse patrimoniale" title="Vue d'ensemble">
          {es.total_declared_value && (
            <p className="font-['Work_Sans'] text-[14px] text-[#1A2530] mb-1">
              <span className="opacity-70">Valeur totale déclarée · </span>
              <span className="font-semibold">{es.total_declared_value}</span>
              {es.total_value_context && <span className="opacity-70 italic"> ({es.total_value_context})</span>}
            </p>
          )}
          {es.assets_count !== null && es.assets_count !== undefined && (
            <p className="font-['Work_Sans'] text-[14px] text-[#1A2530] mb-1">
              <span className="opacity-70">Nombre de biens · </span>
              {es.assets_count}
            </p>
          )}
          {es.usufruit_nue_note && (
            <p className="font-['Work_Sans'] text-[13px] text-[#2C363F] italic mt-2">
              {es.usufruit_nue_note}
            </p>
          )}
          {has(es.heirs_shares) && (
            <ul className="mt-3 space-y-1">
              {es.heirs_shares.map((h, idx) => (
                <li key={idx} className="font-['Work_Sans'] text-[13px] text-[#1A2530]">
                  • <span className="font-semibold">{h.name}</span>
                  {h.share_description && <> — {h.share_description}</>}
                  {h.rights_received && <span className="opacity-70"> · {h.rights_received}</span>}
                  {h.value_summary && <span className="opacity-70"> · {h.value_summary}</span>}
                </li>
              ))}
            </ul>
          )}
        </DossierSection>
      )}

      {/* Timeline */}
      {has(analysis.timeline) && (
        <DossierSection eyebrow="Chronologie" title="Dates clés et étapes">
          <ul className="space-y-2.5">
            {analysis.timeline.map((ev, idx) => (
              <li key={idx} className="font-['Work_Sans'] text-[14px] text-[#1A2530] leading-snug">
                <span className="font-semibold">{ev.date || 'Date non précisée'}</span>
                {' — '}
                {ev.label}
                {ev.status && <span className="opacity-70 italic"> ({ev.status})</span>}
                {ev.detail && (
                  <span className="block text-[13px] text-[#2C363F] italic mt-0.5">{ev.detail}</span>
                )}
              </li>
            ))}
          </ul>
        </DossierSection>
      )}

      {/* Next actions */}
      {has(analysis.next_actions) && (
        <DossierSection eyebrow="Prochaines étapes" title="Actions à mener">
          <ul className="space-y-3">
            {analysis.next_actions.map((na, idx) => (
              <li key={idx} className="p-3 rounded-xl bg-white border border-[#E2DBCF]">
                <div className="flex flex-wrap items-center gap-1.5 mb-1">
                  {na.classification && (
                    <span className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.18em] text-[#1A2530] bg-[#E3D5CA] px-2 py-0.5 rounded-full">
                      {na.classification}
                    </span>
                  )}
                  <span className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] opacity-70">
                    {(na.priority || 'moyenne').toUpperCase()}
                  </span>
                </div>
                <p className="font-['Work_Sans'] font-semibold text-[14px] text-[#1A2530] leading-snug">
                  {na.title}
                </p>
                {na.detail && (
                  <p className="font-['Work_Sans'] text-[13px] text-[#2C363F] mt-1 leading-snug">
                    {na.detail}
                  </p>
                )}
                {na.basis_explanation && (
                  <p className="font-['Work_Sans'] text-[12px] text-[#2C363F] italic mt-1 leading-snug opacity-90">
                    Pourquoi cette action ? {na.basis_explanation}
                  </p>
                )}
                {na.consequence_plain && (
                  <p className="font-['Work_Sans'] text-[12px] text-[#2D6A4F] italic mt-1 leading-snug">
                    → {na.consequence_plain}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </DossierSection>
      )}

      {/* Questions for notary — priority items first */}
      {has(analysis.questions_for_notary) && (
        <DossierSection eyebrow="Questions à poser au notaire" title="Préparez votre rendez-vous">
          <ul className="space-y-2.5">
            {[...analysis.questions_for_notary]
              .sort((a, b) => (b?.is_priority ? 1 : 0) - (a?.is_priority ? 1 : 0))
              .map((q, idx) => (
              <li key={idx} className="font-['Work_Sans'] text-[14px] text-[#1A2530] leading-snug">
                <span className="font-semibold">{q.category || 'Général'}</span>
                {q.is_priority && (
                  <span className="font-['IBM_Plex_Mono'] text-[9px] uppercase tracking-[0.18em] text-[#1A2530] bg-[#E3D5CA] px-1.5 py-0.5 rounded-full ml-1.5">
                    Prioritaire
                  </span>
                )}
                <span> — {q.question}</span>
                {q.rationale && (
                  <p className="text-[12px] text-[#2C363F] italic opacity-90 mt-0.5">
                    {q.rationale}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </DossierSection>
      )}

      {/* Sources */}
      {has(analysis.document_extracts) && (
        <DossierSection eyebrow="Sources du document" title="Extraits utilisés pour l'analyse">
          <ul className="space-y-3">
            {analysis.document_extracts.map((ex, idx) => (
              <li key={idx}>
                <p className="font-['Work_Sans'] font-semibold text-[13px] text-[#1A2530]">
                  {ex.label}
                  {ex.source_page && <span className="opacity-60 font-normal"> — p. {ex.source_page}</span>}
                </p>
                <p className="font-['Work_Sans'] text-[13px] text-[#2C363F] italic leading-relaxed">
                  {String(ex.content || '').slice(0, 600)}
                </p>
              </li>
            ))}
          </ul>
        </DossierSection>
      )}

      <p className="font-['Work_Sans'] text-[11px] text-[#2C363F] italic mt-8 pt-6 border-t border-[#E2DBCF] leading-relaxed">
        Hérita — Lecture documentaire augmentée. Ce dossier est généré à partir
        du document fourni. Il ne se substitue pas à l'avis de votre notaire.
      </p>
    </div>
  );
};

// ===== Financial Synthesis Card — top of personal reading =====

// ===== Personal Identification Checklist — premium only, real values =====
// Builds a "Ce que nous avons identifié pour vous" tick-list from REAL data.
const PersonalIdentificationChecklist = ({
  personal,
  analysis,
  selectedParty,
}) => {
  if (!personal) return null;
  const role = personal.detected_role || 'identifié dans cet acte';
  const roleCanon = normalizeRole(personal.detected_role);

  // Count assets that concern the selected party
  const myAssetsCount = (analysis?.assets || []).filter((a) => {
    if (!a) return false;
    if (a.beneficiary && partyMentioned(a.beneficiary, selectedParty)) return true;
    if (Array.isArray(a.beneficiaries)) {
      return a.beneficiaries.some((b) => partyMentioned(b?.name, selectedParty));
    }
    return false;
  }).length;

  const primaryAmount = personal.financial_synthesis?.primary_amount
    || personal.value_received
    || personal.estimated_personal_value
    || null;

  const fiscalStatus = personal.financial_synthesis?.fiscal_status
    || personal.taxes_due
    || null;

  const items = [
    {
      key: 'role',
      text: `Vous êtes ${role.toLowerCase().startsWith('le ') || role.toLowerCase().startsWith('la ') ? role : `«\u00A0${role}\u00A0»`} dans cet acte`,
    },
  ];
  if (myAssetsCount > 0) {
    items.push({
      key: 'assets',
      text: myAssetsCount === 1
        ? '1 bien vous concerne directement'
        : `${myAssetsCount} biens vous concernent directement`,
    });
  }
  if (primaryAmount && primaryAmount !== 'Non identifié dans le document') {
    const label = personal.financial_synthesis?.primary_label
      || (roleCanon === 'vendeur' ? 'Valeur totale de la vente'
        : roleCanon === 'acquereur' ? 'Prix à régler'
        : roleCanon === 'heritier' ? 'Quote-part successorale identifiée'
        : 'Valeur patrimoniale identifiée');
    items.push({
      key: 'amount',
      text: `${label}\u00A0: ${primaryAmount}`,
    });
  }
  if (fiscalStatus) {
    items.push({
      key: 'fiscal',
      text: fiscalStatus,
    });
  }
  if (personal.immediate_action && !NO_ACTION_RE.test(personal.immediate_action)) {
    items.push({
      key: 'action',
      text: 'Une démarche reste à effectuer après la lecture de cet acte',
    });
  } else if (personal.immediate_action) {
    items.push({
      key: 'action-calm',
      text: 'Aucune démarche immédiate identifiée à votre charge',
    });
  }
  if (items.length < 3) return null;

  return (
    <Card
      className="bg-white rounded-2xl p-6 sm:p-8 border border-[#1A2530]/15 shadow-md"
      data-testid="personal-identification-checklist"
    >
      <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#2C363F] opacity-80 mb-2">
        Ce que nous avons identifié pour vous
      </p>
      <h3 className="font-['Cormorant_Garamond'] font-medium text-[22px] sm:text-[26px] text-[#1A2530] leading-tight mb-5">
        {selectedParty}, voici l'essentiel
      </h3>
      <ul className="space-y-2.5">
        {items.map((it) => (
          <li
            key={it.key}
            className="flex items-start gap-3 p-3 rounded-xl bg-[#F0F4EE]/70 border border-[#2D6A4F]/15"
            data-testid={`identification-${it.key}`}
          >
            <div className="w-6 h-6 rounded-full bg-[#2D6A4F] flex items-center justify-center flex-shrink-0 mt-0.5">
              <CircleCheck className="h-3.5 w-3.5 text-white" />
            </div>
            <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#1A2530] leading-snug">
              {it.text}
            </p>
          </li>
        ))}
      </ul>
    </Card>
  );
};

// =============================================================================
// v9 — SUCCESSION-SPECIFIC OVERVIEW CARD (March 2026)
// =============================================================================
// Renders ONLY when `analysis.document_category` contains "succession" AND
// the LLM populated `succession_essentials`. Lives in the NEUTRAL (global)
// dossier view to answer "Qui décédé / Qui hérite / Que comprend la
// succession / Y a-t-il des droits / Pourquoi cette déclaration ?" in
// 5 seconds without any nominative personal reading.
// =============================================================================
const SuccessionOverviewCard = ({ analysis }) => {
  const cat = String(analysis?.document_category || '').toLowerCase();
  if (!cat.includes('succession')) return null;
  const ess = analysis.succession_essentials || null;
  const summary = analysis.succession_plain_summary;
  if (!ess && !summary) return null;

  return (
    <Card
      className="bg-white rounded-2xl p-6 sm:p-9 border-2 border-[#1A2530] shadow-md"
      data-testid="succession-overview-card"
    >
      <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#2C363F] opacity-85 mb-2">
        Cette succession en clair
      </p>
      <h2 className="font-['Cormorant_Garamond'] font-medium text-[26px] sm:text-[32px] text-[#1A2530] leading-tight mb-5">
        Ce que cette succession signifie concrètement
      </h2>

      {summary && (
        <p
          className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#1A2530] leading-relaxed mb-5"
          data-testid="succession-plain-summary"
        >
          {summary}
        </p>
      )}

      {ess && (
        <div className="space-y-3">
          {(ess.deceased || ess.death_date) && (
            <div
              className="p-4 rounded-xl bg-[#F5EFE6] border border-[#E3D5CA]"
              data-testid="succession-deceased"
            >
              <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.20em] text-[#2C363F] mb-1 opacity-80">
                Personne décédée
              </p>
              <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#1A2530] leading-snug">
                {ess.deceased || 'Non précisé dans le document'}
                {ess.death_date && (
                  <span className="opacity-70"> — {ess.death_date}</span>
                )}
              </p>
            </div>
          )}

          {has(ess.heirs) && (
            <div
              className="p-4 rounded-xl bg-white border border-[#1A2530]/15"
              data-testid="succession-heirs"
            >
              <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.20em] text-[#2C363F] mb-2 opacity-80">
                Héritiers identifiés
              </p>
              <ul className="space-y-1.5">
                {ess.heirs.map((h, i) => (
                  <li
                    key={i}
                    className="font-['Work_Sans'] text-[14px] text-[#1A2530] leading-snug flex items-start gap-2"
                    data-testid={`succession-heir-${i}`}
                  >
                    <span className="text-[#2D6A4F] font-bold mt-0.5">·</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(ess.net_estate_value || ess.estate_composition_summary) && (
            <div
              className="p-4 rounded-xl bg-white border border-[#1A2530]/15"
              data-testid="succession-estate"
            >
              <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.20em] text-[#2C363F] mb-2 opacity-80">
                Composition de la succession
              </p>
              {ess.net_estate_value && (
                <p className="font-['Cormorant_Garamond'] text-[20px] sm:text-[22px] text-[#1A2530] font-semibold leading-tight mb-1">
                  {ess.net_estate_value}{' '}
                  <span className="font-['Work_Sans'] text-[12px] text-[#2C363F] opacity-70 font-normal">
                    actif net successoral
                  </span>
                </p>
              )}
              {ess.estate_composition_summary && (
                <p className="font-['Work_Sans'] text-[13px] sm:text-[14px] text-[#1A2530] leading-snug">
                  {ess.estate_composition_summary}
                </p>
              )}
            </div>
          )}

          {(ess.rights_due_summary || ess.rights_due_explanation) && (
            <div
              className="p-4 rounded-xl bg-[#1A2530] text-white"
              data-testid="succession-rights"
            >
              <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.20em] text-[#E3D5CA] mb-1 opacity-90">
                Droits de succession
              </p>
              <p className="font-['Cormorant_Garamond'] text-[20px] sm:text-[22px] text-white font-semibold leading-tight">
                {ess.rights_due_summary || 'Non calculé dans le document'}
              </p>
              {ess.rights_due_explanation && (
                <p className="font-['Work_Sans'] text-[13px] text-[#E3D5CA] mt-1 leading-snug">
                  {ess.rights_due_explanation}
                </p>
              )}
            </div>
          )}

          {ess.has_representation && ess.representation_explanation && (
            <div
              className="p-4 rounded-xl bg-amber-50 border border-amber-200"
              data-testid="succession-representation"
            >
              <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.20em] text-amber-900 mb-1 opacity-90">
                Représentation successorale
              </p>
              <p className="font-['Work_Sans'] text-[13px] sm:text-[14px] text-amber-900 leading-snug">
                {ess.representation_explanation}
              </p>
            </div>
          )}

          {has(ess.contextual_notes) && (
            <div className="pt-3 border-t border-[#E2DBCF]">
              <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.20em] text-[#2C363F] mb-2 opacity-80">
                À garder en tête
              </p>
              <ul className="space-y-1.5">
                {ess.contextual_notes.map((n, i) => (
                  <li
                    key={i}
                    className="font-['Work_Sans'] text-[13px] text-[#2C363F] leading-snug italic flex items-start gap-2"
                  >
                    <span className="opacity-60 mt-0.5">→</span>
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

// =============================================================================
// v8 — UNIFIED HEADLINE COMPONENTS (March 2026)
// =============================================================================
// The previous v6/v7 layout had 3 stacked cards that repeated the same info:
//   - WhatActMeansCard ("Ce que cet acte représente pour vous")
//   - KeyUnderstandingsCard ("Les points validés")
//   - PersonalIdentificationChecklist ("Voici l'essentiel")
// The v8 directive merges them into a single "Ce que vous devez retenir" card
// and adds a new "Impact concret sur votre patrimoine" before/after card.
// The old components are kept (definitions below) but no longer rendered.
// =============================================================================

// "Ce que vous devez retenir" — single merged top card.
// =============================================================================
// v10 — INSTANT-CLARITY ONE-LINER (March 2026)
// =============================================================================
// Top of personal block. Renders the LLM-generated `personal_one_liner`,
// a single sentence ≤ 30 words that answers in one breath: qui je suis,
// que je reçois, dois-je payer, avec qui je partage.
// =============================================================================
const PersonalOneLinerCard = ({ personal }) => {
  if (!personal?.personal_one_liner) return null;
  return (
    <div
      className="rounded-2xl p-6 sm:p-7 bg-[#1A2530] text-white shadow-lg"
      data-testid="personal-one-liner-card"
    >
      <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#E3D5CA] mb-2 opacity-90">
        En une phrase
      </p>
      <p className="font-['Cormorant_Garamond'] text-[22px] sm:text-[28px] text-white font-medium leading-[1.25]">
        {personal.personal_one_liner}
      </p>
      {personal.final_situation_concrete && (
        <p
          className="mt-4 pt-4 border-t border-white/15 font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#E3D5CA] leading-relaxed"
          data-testid="personal-final-situation"
        >
          {personal.final_situation_concrete}
        </p>
      )}
    </div>
  );
};

const KeyTakeawayCard = ({ personal, analysis, selectedParty }) => {
  if (!personal) return null;

  // Build the bullets: receive/transmit + key_understandings (deduped) +
  // top fiscal + immediate action signal.
  const dedup = new Set();
  const push = (text) => {
    if (!text) return;
    const k = String(text).trim().toLowerCase();
    if (k.length < 5 || dedup.has(k)) return;
    dedup.add(k);
    bullets.push(String(text).trim());
  };
  const bullets = [];

  // Role line
  const role = personal.detected_role;
  if (role) push(`Vous êtes ${role.toLowerCase().startsWith('le ') || role.toLowerCase().startsWith('la ') ? role : `«\u00A0${role}\u00A0»`} dans cet acte`);

  // What you receive / transmit / keep / lose (only filled ones)
  push(personal.what_you_receive && `Vous recevez : ${personal.what_you_receive}`);
  push(personal.what_you_transmit && `Vous transmettez : ${personal.what_you_transmit}`);
  push(personal.what_you_keep && `Vous conservez : ${personal.what_you_keep}`);
  push(personal.what_you_lose && `Vous perdez : ${personal.what_you_lose}`);

  // Key understandings from the LLM (already validated)
  (personal.key_understandings || []).forEach(push);

  // Fiscal + action signal (only if not already covered)
  if (personal.taxes_due) push(personal.taxes_due);

  // Bien count
  const myAssetsCount = (analysis?.assets || []).filter((a) => {
    if (!a) return false;
    if (a.beneficiary && partyMentioned(a.beneficiary, selectedParty)) return true;
    if (Array.isArray(a.beneficiaries)) {
      return a.beneficiaries.some((b) => partyMentioned(b?.name, selectedParty));
    }
    return false;
  }).length;
  if (myAssetsCount === 1) push('1 bien vous concerne directement');
  else if (myAssetsCount > 1) push(`${myAssetsCount} biens vous concernent directement`);

  if (bullets.length === 0) return null;

  return (
    <Card
      className="bg-white rounded-2xl p-6 sm:p-9 border-2 border-[#1A2530] shadow-md"
      data-testid="key-takeaway-card"
    >
      <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#2C363F] opacity-85 mb-2">
        Lecture personnalisée de {selectedParty}
      </p>
      <h2 className="font-['Cormorant_Garamond'] font-medium text-[26px] sm:text-[34px] text-[#1A2530] leading-tight mb-5">
        Ce que vous devez retenir
      </h2>
      <ul className="space-y-2.5">
        {bullets.map((text, idx) => (
          <li
            key={idx}
            className="flex items-start gap-3 p-3 rounded-xl bg-[#F0F4EE]/60 border border-[#2D6A4F]/20"
            data-testid={`key-takeaway-${idx}`}
          >
            <div className="w-6 h-6 rounded-full bg-[#2D6A4F] flex items-center justify-center flex-shrink-0 mt-0.5">
              <CircleCheck className="h-3.5 w-3.5 text-white" />
            </div>
            <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#1A2530] leading-snug">
              {text}
            </p>
          </li>
        ))}
      </ul>
      {personal.practical_consequences && (
        <div
          className="mt-5 p-4 rounded-xl bg-[#F5EFE6] border border-[#E3D5CA]"
          data-testid="key-takeaway-consequences"
        >
          <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.20em] text-[#2C363F] mb-2 opacity-80">
            Concrètement, dans votre vie
          </p>
          <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#1A2530] leading-relaxed">
            {personal.practical_consequences}
          </p>
        </div>
      )}
    </Card>
  );
};

// "Impact concret sur votre patrimoine" — before/after comparison.
const PatrimonialImpactCard = ({ personal, analysis }) => {
  if (!personal) return null;
  const hasContent = Boolean(
    personal.situation_before
    || personal.situation_after
    || (personal.financial_synthesis && personal.financial_synthesis.primary_amount),
  );
  if (!hasContent) return null;

  const fs = personal.financial_synthesis || {};
  const rows = [];
  if (fs.primary_label && fs.primary_amount) {
    rows.push({ label: fs.primary_label, value: fs.primary_amount });
  }
  // Add up to 2 most informative financial rows
  (fs.rows || []).slice(0, 2).forEach((r) => {
    if (r.label && r.value) rows.push({ label: r.label, value: r.value });
  });
  // Fiscal line if present and not redundant
  if (fs.fiscal_status && !rows.some((r) => String(r.value || '').toLowerCase().includes('plus-value'))) {
    rows.push({ label: 'Fiscalité identifiée', value: fs.fiscal_status });
  }

  return (
    <Card
      className="bg-gradient-to-br from-[#FDFBF7] to-[#F5EFE6] rounded-2xl p-6 sm:p-9 border-2 border-[#1A2530] shadow-lg"
      data-testid="patrimonial-impact-card"
    >
      <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#2C363F] opacity-85 mb-2">
        Comparatif patrimonial
      </p>
      <h2 className="font-['Cormorant_Garamond'] font-medium text-[26px] sm:text-[34px] text-[#1A2530] leading-tight mb-6">
        Impact concret sur votre patrimoine
      </h2>
      {(personal.situation_before || personal.situation_after) && (
        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <div className="p-4 rounded-xl bg-white border border-[#1A2530]/15" data-testid="impact-before">
            <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.20em] text-[#2C363F] mb-2 opacity-80">
              Avant cet acte
            </p>
            <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#1A2530] leading-snug">
              {personal.situation_before || 'Situation antérieure non précisée dans le document.'}
            </p>
          </div>
          <div className="p-4 rounded-xl bg-[#1A2530] text-white border border-[#1A2530]" data-testid="impact-after">
            <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.20em] text-[#E3D5CA] mb-2 opacity-90">
              Après cet acte
            </p>
            <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-white leading-snug">
              {personal.situation_after || 'Situation nouvelle non précisée dans le document.'}
            </p>
          </div>
        </div>
      )}
      {rows.length > 0 && (
        <div className="divide-y divide-[#1A2530]/10 bg-white/70 rounded-xl border border-[#1A2530]/15">
          {rows.map((r, idx) => (
            <div
              key={idx}
              className="flex justify-between items-baseline gap-4 px-4 py-3"
              data-testid={`impact-row-${idx}`}
            >
              <p className="font-['Work_Sans'] text-[12px] sm:text-[13px] text-[#2C363F] uppercase tracking-wide">
                {r.label}
              </p>
              <p className="font-['Cormorant_Garamond'] font-semibold text-[18px] sm:text-[20px] text-[#1A2530] text-right">
                {r.value}
              </p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

// ===== "Ce que cet acte représente pour vous" — v6 headline card =====
// Reads `situation_before / situation_after / what_you_receive / transmit /
// keep / lose / practical_consequences` from the LLM. This is the new
// emotional centerpiece of the personalized reading.
const WhatActMeansCard = ({ personal, selectedParty }) => {
  if (!personal) return null;
  const hasBeforeAfter = Boolean(personal.situation_before || personal.situation_after);
  const items = [
    { key: 'receive', label: 'Vous recevez', value: personal.what_you_receive, tone: 'green' },
    { key: 'transmit', label: 'Vous transmettez', value: personal.what_you_transmit, tone: 'amber' },
    { key: 'keep', label: 'Vous conservez', value: personal.what_you_keep, tone: 'neutral' },
    { key: 'lose', label: 'Vous perdez', value: personal.what_you_lose, tone: 'rose' },
  ].filter((it) => it.value && String(it.value).trim().length > 0);
  if (!hasBeforeAfter && items.length === 0 && !personal.practical_consequences) return null;

  const toneClass = {
    green: 'bg-[#F0F4EE] border-[#2D6A4F]/25 text-[#1A2530]',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    neutral: 'bg-[#F5EFE6] border-[#E3D5CA] text-[#1A2530]',
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
  };

  return (
    <Card
      className="bg-gradient-to-br from-[#FDFBF7] to-[#F5EFE6] rounded-2xl p-6 sm:p-9 border-2 border-[#1A2530] shadow-lg"
      data-testid="what-act-means-card"
    >
      <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#2C363F] opacity-85 mb-2">
        Lecture personnelle de {selectedParty}
      </p>
      <h2 className="font-['Cormorant_Garamond'] font-medium text-[26px] sm:text-[34px] text-[#1A2530] leading-tight mb-6">
        Ce que cet acte représente pour vous
      </h2>

      {hasBeforeAfter && (
        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          {personal.situation_before && (
            <div className="p-4 rounded-xl bg-white/70 border border-[#1A2530]/15" data-testid="situation-before">
              <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.20em] text-[#2C363F] mb-2 opacity-80">
                Avant cet acte
              </p>
              <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#1A2530] leading-snug">
                {personal.situation_before}
              </p>
            </div>
          )}
          {personal.situation_after && (
            <div className="p-4 rounded-xl bg-[#1A2530] text-white border border-[#1A2530]" data-testid="situation-after">
              <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.20em] text-[#E3D5CA] mb-2 opacity-90">
                Après cet acte
              </p>
              <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-white leading-snug">
                {personal.situation_after}
              </p>
            </div>
          )}
        </div>
      )}

      {items.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3 mb-5">
          {items.map((it) => (
            <div
              key={it.key}
              className={`p-4 rounded-xl border ${toneClass[it.tone]}`}
              data-testid={`what-you-${it.key}`}
            >
              <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.20em] mb-1.5 opacity-75">
                {it.label}
              </p>
              <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] leading-snug">
                {it.value}
              </p>
            </div>
          ))}
        </div>
      )}

      {personal.practical_consequences && (
        <div
          className="p-4 rounded-xl bg-white border border-[#1A2530]/15"
          data-testid="practical-consequences"
        >
          <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.20em] text-[#2C363F] mb-2 opacity-80">
            Concrètement, dans votre vie
          </p>
          <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#1A2530] leading-relaxed">
            {personal.practical_consequences}
          </p>
        </div>
      )}
    </Card>
  );
};

// ===== "Ce qu'Hérita a compris" — validated key understandings =====
const KeyUnderstandingsCard = ({ understandings, selectedParty }) => {
  const items = Array.isArray(understandings) ? understandings.filter(Boolean) : [];
  if (items.length === 0) return null;
  return (
    <Card
      className="bg-white rounded-2xl p-6 sm:p-8 border border-[#2D6A4F]/25 shadow-sm"
      data-testid="key-understandings-card"
    >
      <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#2D6A4F] mb-2 opacity-90">
        Ce qu'Hérita a compris
      </p>
      <h3 className="font-['Cormorant_Garamond'] font-medium text-[22px] sm:text-[26px] text-[#1A2530] leading-tight mb-5">
        Les points validés pour {selectedParty}
      </h3>
      <ul className="grid sm:grid-cols-2 gap-2.5">
        {items.map((text, idx) => (
          <li
            key={idx}
            className="flex items-start gap-3 p-3 rounded-xl bg-[#F0F4EE]/60 border border-[#2D6A4F]/20"
            data-testid={`key-understanding-${idx}`}
          >
            <div className="w-6 h-6 rounded-full bg-[#2D6A4F] flex items-center justify-center flex-shrink-0 mt-0.5">
              <CircleCheck className="h-3.5 w-3.5 text-white" />
            </div>
            <p className="font-['Work_Sans'] text-[14px] text-[#1A2530] leading-snug">
              {text}
            </p>
          </li>
        ))}
      </ul>
    </Card>
  );
};

// ===== "Vos 5 vraies questions" — top personal questions =====
const TopPersonalQuestionsCard = ({ questions, selectedParty }) => {
  const items = Array.isArray(questions) ? questions.filter(Boolean).slice(0, 5) : [];
  if (items.length === 0) return null;
  return (
    <Card
      className="bg-[#1A2530] text-white rounded-2xl p-6 sm:p-8 border border-[#1A2530]"
      data-testid="top-personal-questions-card"
    >
      <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#E3D5CA] mb-2 opacity-90">
        Vos vraies questions
      </p>
      <h3 className="font-['Cormorant_Garamond'] font-medium text-[22px] sm:text-[26px] text-white leading-tight mb-5">
        Ce que vous vous demandez en lisant ce document
      </h3>
      <ol className="space-y-2">
        {items.map((q, idx) => (
          <li
            key={idx}
            className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10"
            data-testid={`top-personal-question-${idx}`}
          >
            <span className="font-['IBM_Plex_Mono'] text-[12px] text-[#E3D5CA] font-medium w-6 flex-shrink-0">
              {String(idx + 1).padStart(2, '0')}.
            </span>
            <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-white leading-snug">
              {q}
            </p>
          </li>
        ))}
      </ol>
    </Card>
  );
};

// ===== Financial Synthesis Card — top of personal reading =====
const FinancialSynthesisCard = ({ synth, partyName }) => {
  if (!synth) return null;
  const hasPrimary = synth.primary_amount;
  const hasRows = Array.isArray(synth.rows) && synth.rows.length > 0;
  const hasFiscal = synth.fiscal_status || synth.fiscal_note;
  if (!hasPrimary && !hasRows && !hasFiscal) return null;
  return (
    <Card
      className="bg-white rounded-2xl p-6 sm:p-9 border border-[#1A2530]/15 shadow-md relative overflow-hidden"
      data-testid="financial-synthesis"
    >
      <div
        className="absolute top-0 left-0 right-0 h-1"
        style={{ background: 'linear-gradient(90deg, #1A2530 0%, #E3D5CA 100%)' }}
      />
      <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#2C363F] mb-1.5 opacity-80">
        Synthèse financière {partyName ? `· ${partyName}` : ''}
      </p>
      <h2 className="font-['Cormorant_Garamond'] font-medium text-[26px] sm:text-[32px] text-[#1A2530] leading-tight mb-6">
        Ce que cet acte représente pour vous
      </h2>

      {hasPrimary && (
        <div className="mb-7 pb-7 border-b border-[#E2DBCF]">
          <p className="font-['Work_Sans'] text-[11px] uppercase tracking-[0.22em] text-[#2C363F] opacity-80 mb-2">
            {synth.primary_label || 'Montant principal'}
          </p>
          <p className="font-['Cormorant_Garamond'] font-semibold text-[#1A2530] leading-none text-[44px] sm:text-[64px] lg:text-[72px] break-words">
            {synth.primary_amount}
          </p>
          {synth.primary_context && (
            <p className="font-['Work_Sans'] text-[12px] sm:text-[13px] text-[#2C363F] italic opacity-80 mt-3 leading-snug">
              {synth.primary_context}
            </p>
          )}
        </div>
      )}

      {hasRows && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          {synth.rows.map((r, idx) => (
            <div
              key={idx}
              className="p-4 rounded-xl bg-[#F9F7F3] border border-[#E2DBCF]"
              data-testid={`finsynth-row-${idx}`}
            >
              <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] opacity-80 mb-1.5">
                {r.label}
              </p>
              <p className="font-['Cormorant_Garamond'] font-semibold text-[20px] sm:text-[22px] text-[#1A2530] leading-tight break-words">
                {r.value}
              </p>
              {r.hint && (
                <p className="font-['Work_Sans'] text-[12px] text-[#2C363F] italic opacity-80 mt-1 leading-snug">
                  {r.hint}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {hasFiscal && (
        <div
          className="p-4 rounded-xl bg-[#F0F4EE] border border-[#2D6A4F]/20"
          data-testid="finsynth-fiscal"
        >
          <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.22em] text-[#2D6A4F] mb-1.5">
            Situation fiscale
          </p>
          {synth.fiscal_status && (
            <p className="font-['Work_Sans'] font-semibold text-[15px] sm:text-[16px] text-[#1A2530] leading-snug">
              {synth.fiscal_status}
            </p>
          )}
          {synth.fiscal_note && (
            <p className="font-['Work_Sans'] text-[13px] text-[#2C363F] leading-snug mt-1">
              {synth.fiscal_note}
            </p>
          )}
        </div>
      )}
    </Card>
  );
};

// ----- Main -----

const DashboardPageV2 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [doc, setDoc] = useState(null);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [extractsOpen, setExtractsOpen] = useState(false);
  const [selectedParty, setSelectedParty] = useState(null);
  const [printMode, setPrintMode] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [dossierUrl, setDossierUrl] = useState(null);
  const [accessOfferOpen, setAccessOfferOpen] = useState(false);

  useEffect(() => {
    // 1) The classic post-upload flow pushes the analysis through
    //    location.state — use it directly so we don't re-fetch.
    if (location.state?.analysis) {
      setAnalysis(location.state.analysis);
      setDoc(location.state.document);
      return;
    }
    // 2) Deep-link re-entry: ?dossier=<id> (e.g. user came back via a
    //    copied success-page link, or via Stripe's cancel_url). We fetch
    //    the analysis directly so they don't have to re-upload.
    const sp = new URLSearchParams(location.search);
    const dossierId = sp.get('dossier');
    const paymentParam = sp.get('payment');
    if (dossierId) {
      (async () => {
        try {
          const { data } = await axios.get(`${API}/analyses/${dossierId}`);
          setAnalysis(data);
          setDoc({
            id: data.document_id,
            filename: data.document_filename || 'document.pdf',
          });
          if (paymentParam === 'cancelled') {
            toast('Paiement annulé', {
              description: 'Votre analyse est conservée. Vous pouvez reprendre le paiement à tout moment.',
            });
          }
        } catch (e) {
          toast.error("Dossier introuvable", {
            description: e?.response?.data?.detail || 'Veuillez recommencer l\u2019upload.',
          });
          navigate('/upload');
        }
      })();
      return;
    }
    // 3) Nothing to render — go back to upload.
    navigate('/upload');
  }, [location, navigate]);

  useEffect(() => {
    if (!analysis?.document_id) return;
    trackViewItem({
      documentId: analysis.document_id,
      documentType: analysis.document_type,
      isPremium: Boolean(analysis.is_premium_unlocked),
    });
  }, [analysis?.document_id, analysis?.is_premium_unlocked, analysis?.document_type]);

  if (!analysis) return null;

  // ===== Freemium gating =====
  const isPremium = Boolean(analysis.is_premium_unlocked);

  // Premium unlock entry point used by the teaser & the PremiumCTACard.
  // We open the pre-checkout reassurance modal first (shows the user
  // exactly what they buy + key guarantees), then `confirmAccessOffer`
  // posts to /api/payments/checkout and redirects to Stripe.
  const requestPremiumUnlock = () => {
    // GA4 — begin_checkout (user clicked "Débloquer ma lecture")
    if (analysis?.document_id) {
      trackBeginCheckout({
        documentId: analysis.document_id,
        documentType: analysis.document_type,
      });
    }
    setAccessOfferOpen(true);
  };

  // Triggered from inside the reassurance modal. Calls the Stripe-backed
  // checkout endpoint and redirects to the Stripe-hosted page. If the
  // backend reports the dossier is already unlocked (e.g. user came back
  // after a previous successful payment), we just refresh the analysis
  // locally instead of opening Stripe again — they never repay for the
  // same file.
  const confirmAccessOffer = async () => {
    if (!analysis?.document_id) return;
    setUnlocking(true);
    try {
      const { data } = await axios.post(
        `${API}/payments/checkout/${analysis.document_id}`,
        {
          package_id: 'herita_dossier_premium',
          origin_url: window.location.origin,
        },
      );
      if (data.already_unlocked) {
        setAccessOfferOpen(false);
        const refreshed = await axios.get(
          `${API}/analyses/${analysis.document_id}`,
        );
        setAnalysis(refreshed.data);
        toast.success('Dossier déjà débloqué — bienvenue.');
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        return;
      }
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error('URL Stripe absente.');
    } catch (e) {
      toast.error("Le paiement n'a pas pu démarrer", {
        description:
          e?.response?.data?.detail ||
          'Vérifiez votre connexion et réessayez dans un instant.',
      });
    } finally {
      setUnlocking(false);
    }
  };

  const handleDownloadDossier = async () => {
    if (!analysis?.document_id || !isPremium) return;
    setDossierUrl('inline');
  };

  const handleCloseDossier = () => {
    setDossierUrl(null);
  };

  // Focus mode helpers
  const inFocus = Boolean(selectedParty);
  const transferConcernsMe = (t) =>
    !inFocus ||
    partyMentioned(t.donor, selectedParty) ||
    partyMentioned(t.recipient, selectedParty);
  const assetConcernsMe = (a) => {
    if (!inFocus) return true;
    if (partyMentioned(a.beneficiary, selectedParty)) return true;
    return (a.beneficiaries || []).some((b) => partyMentioned(b.name, selectedParty));
  };
  const personalCardIsMine = (p) =>
    !inFocus || normalize(p.party_name) === normalize(selectedParty);

  // Selected party's personal_relevance entry (for "Ce que vous recevez personnellement" block)
  const selectedPersonal =
    inFocus
      ? (analysis.personal_relevance || []).find(
          (p) => normalize(p.party_name) === normalize(selectedParty)
        )
      : null;

  // Focus-aware text matching for timeline + actions
  const textConcernsMe = (...texts) =>
    !inFocus || texts.some((t) => partyMentioned(t, selectedParty));

  const handlePrintPersonal = () => {
    // The "Exporter ma lecture" button must produce the same premium
    // Dossier Hérita PDF as the dialog's "Télécharger en PDF" — not a
    // browser-print summary. We trigger the backend endpoint with the
    // currently selected party so the PDF opens in personal mode.
    if (!analysis?.document_id) return;
    const url =
      `${API}/analyses/${analysis.document_id}/dossier.pdf` +
      (selectedParty ? `?party=${encodeURIComponent(selectedParty)}` : '');
    // Use an invisible anchor so the browser performs a real download
    // (works on Safari + suppresses the navigation warning on Chrome).
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Dossier-Herita.pdf';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // ===== Print view =====
  if (printMode && selectedPersonal) {
    const myTransfers = (analysis.transfers || []).filter(transferConcernsMe);
    const myAssets = (analysis.assets || []).filter(assetConcernsMe);
    return (
      <TooltipProvider>
      <div className="min-h-screen bg-white p-8 sm:p-12 max-w-4xl mx-auto" data-clarity-mask="True" data-testid="print-view">
        <style>{`
          @media print {
            @page { margin: 12mm; size: A4; }
            body { background: white !important; }
            .no-print, [data-radix-popper-content-wrapper] { display: none !important; }
          }
        `}</style>

        <div className="border-b border-[#E2DBCF] pb-5 mb-6">
          <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#2C363F] mb-1">
            Hérita — Lecture personnalisée
          </p>
          <h1 className="font-['Cormorant_Garamond'] text-[28px] sm:text-[34px] text-[#1A2530] leading-tight mt-1">
            {selectedParty}
          </h1>
          <p className="font-['Work_Sans'] text-[13px] text-[#2C363F] mt-2">
            {analysis.document_category || 'Document juridique'}
            {analysis.document_date ? ` · ${analysis.document_date}` : ''}
            {' · '}Lecture générée le {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Synthesis */}
        {analysis.act_synthesis && (
          <section className="mb-6">
            <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] mb-2">
              En 30 secondes — ce que fait cet acte
            </p>
            <p className="font-['Work_Sans'] text-[14px] text-[#1A2530] leading-relaxed">
              {analysis.act_synthesis}
            </p>
          </section>
        )}

        {/* Personal block */}
        <section className="mb-6 p-5 rounded-xl border border-[#1A2530]/30 bg-[#F9F7F3]">
          <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] mb-2">
            Ce que vous recevez personnellement
          </p>
          {selectedPersonal.legal_mechanism && (
            <p className="font-['Work_Sans'] text-[13px] text-[#1A2530] mb-1">
              <span className="font-semibold">Mécanisme juridique : </span>
              {selectedPersonal.legal_mechanism}
            </p>
          )}
          {selectedPersonal.legal_mechanism_explanation && (
            <p className="font-['Work_Sans'] text-[13px] text-[#2C363F] mb-3 leading-relaxed italic">
              {selectedPersonal.legal_mechanism_explanation}
            </p>
          )}
          {selectedPersonal.personal_summary && (
            <p className="font-['Work_Sans'] text-[14px] text-[#1A2530] leading-relaxed">
              {selectedPersonal.personal_summary}
            </p>
          )}
          {selectedPersonal.estimated_personal_value && (
            <p className="font-['Work_Sans'] text-[13px] text-[#1A2530] mt-3">
              <span className="font-semibold">Valeur retenue : </span>
              {selectedPersonal.estimated_personal_value}
            </p>
          )}
        </section>

        {/* My assets */}
        {myAssets.length > 0 && (
          <section className="mb-6">
            <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] mb-3">
              Vos biens, vos droits
            </p>
            <div className="space-y-3">
              {myAssets.map((a, idx) => (
                <div key={idx} className="p-4 border border-[#E2DBCF] rounded-lg">
                  <p className="font-['Work_Sans'] font-semibold text-[14px] text-[#1A2530]">{a.name}</p>
                  {a.address && (
                    <p className="font-['Work_Sans'] text-[12px] text-[#2C363F]">{a.address}</p>
                  )}
                  <div className="mt-2 grid grid-cols-2 gap-2 text-[12px] text-[#1A2530]">
                    {a.value && (
                      <div>
                        <span className="text-[#2C363F]">Valeur · </span>
                        {a.value} {a.value_context ? `(${a.value_context})` : ''}
                      </div>
                    )}
                    {a.rights_transferred && (
                      <div>
                        <span className="text-[#2C363F]">Droit · </span>
                        {a.rights_transferred}
                      </div>
                    )}
                  </div>
                  {has(a.beneficiaries) && (
                    <ul className="mt-2 text-[12px] text-[#1A2530] space-y-0.5">
                      {a.beneficiaries.map((b, j) => (
                        <li key={j}>
                          · {b.name} — {b.right || '—'} {b.share ? `(${b.share})` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                  {a.source_page && (
                    <p className="font-['IBM_Plex_Mono'] text-[10px] text-[#2C363F] mt-2 uppercase tracking-[0.1em]">
                      Source · p. {a.source_page}
                      {a.source_reference ? ` · ${a.source_reference}` : ''}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* My transfers */}
        {myTransfers.length > 0 && (
          <section className="mb-6">
            <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] mb-3">
              Relations qui vous concernent
            </p>
            <ul className="space-y-2">
              {myTransfers.map((t, idx) => (
                <li key={idx} className="font-['Work_Sans'] text-[13px] text-[#1A2530] border-l-2 border-[#1A2530] pl-3">
                  <span className="font-semibold">{t.donor}</span>
                  {' → '}
                  <span className="font-semibold">{t.recipient}</span>
                  {' · '}
                  {t.asset || '—'}
                  {t.right ? ` · ${t.right}` : ''}
                  {t.share ? ` · ${t.share}` : ''}
                  {t.source_page ? ` (p. ${t.source_page})` : ''}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Next actions adapted */}
        {has(analysis.next_actions) && (
          <section className="mb-6">
            <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] mb-3">
              Pour avancer
            </p>
            <ol className="space-y-2 list-decimal pl-5">
              {analysis.next_actions.map((n, idx) => (
                <li key={idx} className="font-['Work_Sans'] text-[13px] text-[#1A2530]">
                  <span className="font-semibold">{n.title}</span>
                  {n.detail ? ` — ${n.detail}` : ''}
                  {' '}
                  <span className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-wider text-[#2C363F]">
                    [{n.basis === 'document' ? 'document' : 'recommandation à confirmer'}{n.source_page ? `, p. ${n.source_page}` : ''}]
                  </span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* My questions for the notary */}
        {has(analysis.questions_for_notary) && (
          <section className="mb-6">
            <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] mb-3">
              Questions utiles à poser à votre notaire
            </p>
            <ol className="space-y-2 list-decimal pl-5">
              {analysis.questions_for_notary.map((q, idx) => (
                <li key={idx} className="font-['Work_Sans'] text-[13px] text-[#1A2530] italic">
                  « {q.question} »
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Uncertainties */}
        {has(analysis.uncertainties) && (
          <section className="mb-6">
            <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] mb-3">
              Points qui mériteraient confirmation
            </p>
            <ul className="space-y-1.5 list-disc pl-5">
              {analysis.uncertainties.map((u, idx) => (
                <li key={idx} className="font-['Work_Sans'] text-[12px] text-[#2C363F] leading-relaxed">
                  {u}
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-8 pt-4 border-t border-[#E2DBCF] flex justify-between items-center text-[11px] text-[#2C363F]">
          <p className="font-['Work_Sans'] italic">
            Hérita ne se substitue pas à votre notaire. Cette lecture est une aide à la compréhension.
          </p>
          <p className="font-['IBM_Plex_Mono'] uppercase tracking-[0.18em]">
            Lecture assistée · Hérita
          </p>
        </div>

        <div className="no-print mt-8 flex gap-3 justify-center print:hidden">
          <button
            onClick={() => window.print()}
            className="bg-[#1A2530] text-white rounded-full px-4 py-2 font-['Work_Sans'] text-sm flex items-center gap-2"
            data-testid="print-now-btn"
          >
            <Printer className="h-4 w-4" />
            Imprimer maintenant
          </button>
          <button
            onClick={() => setPrintMode(false)}
            className="border border-[#E2DBCF] text-[#1A2530] rounded-full px-4 py-2 font-['Work_Sans'] text-sm"
            data-testid="print-back-btn"
          >
            Retour au dossier
          </button>
        </div>
      </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
    <SEO
      title="Mon dossier notarial — HÉRITA"
      description="Lecture personnalisée de votre document notarial par HÉRITA."
      path="/dashboard"
      noindex
    />
    <div
      data-testid={DASHBOARD.container}
      data-clarity-mask="True"
      className={`min-h-screen bg-[#F9F7F3] ${printMode ? 'print-active' : ''}`}
    >
      <style>{`
        @media print {
          @page { margin: 14mm; }
          body { background: white !important; }
          .print-hide { display: none !important; }
          .print-active .no-print { display: none !important; }
          .print-active .print-only { display: block !important; }
          .print-active .focus-banner-print-hide { display: none !important; }
          [data-radix-popper-content-wrapper] { display: none !important; }
        }
        .print-only { display: none; }
      `}</style>
      <Toaster position="top-center" richColors />

      {/* Header */}
      <div className="bg-white border-b border-[#E2DBCF] py-5 sm:py-6 px-5 sm:px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/upload')}
            className="mb-3 sm:mb-4 text-[#2C363F] hover:text-[#1A2530] font-['Work_Sans'] -ml-2 h-auto py-1"
            data-testid="back-to-upload-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Analyser un autre document
          </Button>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-4 sm:mb-5 pb-4 border-b border-dashed border-[#E2DBCF]">
            <span className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] tracking-[0.12em] text-[#2C363F] uppercase">
              Analyse documentaire
            </span>
            <span className="text-[#D3CCBE] hidden sm:inline">·</span>
            <span className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] tracking-[0.12em] text-[#2C363F] uppercase truncate max-w-[60vw]">
              {doc?.filename || analysis.document_filename || 'Document'}
            </span>
          </div>

          <div className="flex flex-col gap-3">
            <p className="font-['Work_Sans'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#2C363F]">
              Type de dossier détecté
            </p>
            <div className="flex flex-wrap items-center gap-3" data-testid="document-category-badge">
              <h1 className="font-['Cormorant_Garamond'] font-normal text-3xl sm:text-[36px] text-[#1A2530] leading-[1.1]">
                {analysis.document_category || 'Type non identifié'}
              </h1>
              <ConfidenceBadge confidence={analysis.document_category_confidence} />
            </div>
            {!analysis.is_recognized_document && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 mt-2 max-w-2xl">
                <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
                <p className="font-['Work_Sans'] text-[13px] text-amber-900 leading-relaxed">
                  Ce document ne semble pas être un acte juridique français standard. L'analyse reste partielle — votre notaire saura mieux vous orienter.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Focus banner */}
      {inFocus && (
        <div
          className="sticky top-0 z-40 bg-[#1A2530] text-white border-b border-[#2C363F] shadow-sm"
          data-testid="focus-banner"
        >
          <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 lg:px-24 py-3 flex flex-wrap items-center gap-3">
            <div className="w-7 h-7 rounded-full bg-[#E3D5CA] flex items-center justify-center flex-shrink-0">
              <UserCheck className="h-3.5 w-3.5 text-[#1A2530]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.18em] text-[#E3D5CA] opacity-80">
                Lecture personnalisée
              </p>
              <p className="font-['Work_Sans'] text-[14px] font-semibold text-white truncate">
                Vous êtes : {selectedParty}
              </p>
            </div>
            <button
              onClick={handlePrintPersonal}
              className="font-['Work_Sans'] text-[12px] text-[#1A2530] bg-[#E3D5CA] hover:bg-white rounded-full px-3 py-1.5 flex items-center gap-1.5 transition-colors font-medium"
              data-testid="print-personal-btn"
            >
              <Printer className="h-3 w-3" />
              Exporter ma lecture
            </button>
            <button
              onClick={() => setSelectedParty(null)}
              className="font-['Work_Sans'] text-[12px] text-[#E3D5CA] hover:text-white border border-white/20 hover:border-white/50 rounded-full px-3 py-1.5 flex items-center gap-1.5 transition-colors"
              data-testid="exit-focus-btn"
            >
              <X className="h-3 w-3" />
              Voir tout le document
            </button>
          </div>
        </div>
      )}

      <div className="py-10 sm:py-12 px-5 sm:px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto space-y-8">

          {/* v9 — Succession-specific global overview, shown at the very
              top of the dashboard whenever the document is a succession
              declaration. Document-centric, no nominative info. */}
          <SuccessionOverviewCard analysis={analysis} />

          {/* "Qui êtes-vous ?" — ALWAYS visible. Lets the user pick or
              change their identity in the document at any moment. */}
          {(analysis.personal_relevance || []).length >= 1 && (
            <Card
              className="bg-white rounded-2xl p-6 sm:p-8 border-2 border-[#1A2530] shadow-md"
              data-testid="who-are-you"
            >
              <p className="font-['Work_Sans'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#2C363F] mb-2">
                Étape essentielle
              </p>
              <h2 className="font-['Cormorant_Garamond'] font-medium text-2xl sm:text-3xl text-[#1A2530] mb-2 leading-tight">
                {selectedParty
                  ? 'Vous lisez ce document en tant que :'
                  : 'Qui êtes-vous dans ce document ?'}
              </h2>
              <p className="font-['Work_Sans'] text-[13px] sm:text-sm text-[#2C363F] leading-relaxed mb-5 max-w-2xl">
                {selectedParty
                  ? 'La lecture personnalisée est contextualisée selon votre rôle. Vous pouvez changer d\'identité à tout moment.'
                  : 'Sélectionnez la personne que vous êtes pour recevoir une lecture personnalisée selon votre rôle réel.'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {analysis.personal_relevance.map((p, idx) => {
                  const isActive = selectedParty === p.party_name;
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        setSelectedParty(isActive ? null : p.party_name);
                        if (!isActive && typeof window !== 'undefined') {
                          setTimeout(() => {
                            const el =
                              document.querySelector('[data-testid="financial-synthesis"]')
                              || document.querySelector('[data-testid="premium-teaser"]')
                              || document.querySelector('[data-testid="personal-block-top"]');
                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }, 60);
                        }
                      }}
                      className={`text-left p-4 rounded-xl border transition-all duration-200 group ${
                        isActive
                          ? 'border-[#1A2530] bg-[#1A2530] shadow-md'
                          : 'border-[#E2DBCF] bg-[#F9F7F3]/60 hover:bg-white hover:border-[#1A2530] hover:shadow-md'
                      }`}
                      data-testid={`who-are-you-pick-${idx}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                          isActive ? 'bg-[#E3D5CA]' : 'bg-[#E3D5CA] group-hover:bg-[#1A2530]'
                        }`}>
                          <Users className={`h-4 w-4 ${isActive ? 'text-[#1A2530]' : 'text-[#1A2530] group-hover:text-white'}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`font-['Work_Sans'] font-semibold text-[14px] leading-snug ${isActive ? 'text-white' : 'text-[#1A2530]'}`}>
                            {p.party_name}
                          </p>
                          {p.detected_role && (
                            <p className={`font-['Work_Sans'] text-[12px] leading-snug mt-0.5 ${isActive ? 'text-[#E3D5CA]' : 'text-[#2C363F]'}`}>
                              {p.detected_role}
                            </p>
                          )}
                          <p className={`font-['IBM_Plex_Mono'] text-[9px] uppercase tracking-[0.18em] leading-snug mt-2 ${
                            isActive ? 'text-[#E3D5CA]' : 'text-[#2C363F] opacity-70'
                          }`}>
                            {isActive ? 'Lecture active' : 'Lecture personnalisée disponible'}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {!selectedParty && (
                <button
                  type="button"
                  onClick={() => {
                    const el = document.querySelector('[data-testid="act-synthesis"]');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="font-['Work_Sans'] text-[12px] text-[#2C363F] hover:text-[#1A2530] underline underline-offset-2"
                  data-testid="who-are-you-skip"
                >
                  Voir le document complet
                </button>
              )}
            </Card>
          )}

          {/* Privacy Notice */}
          <div className="flex items-start gap-3 p-4 bg-[#E3D5CA]/30 rounded-xl border border-[#E3D5CA]">
            <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-[#1A2530] mt-0.5 flex-shrink-0" />
            <p className="font-['Work_Sans'] text-xs sm:text-sm text-[#1A2530] leading-relaxed">
              {analysis.privacy_note}
            </p>
          </div>

          {/* Free counts bar — proves Hérita understood the document */}
          {!isPremium && (
            <div data-testid="free-tier-counts">
              <p className="font-['Work_Sans'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#2C363F] mb-3">
                Ce que Hérita a identifié dans votre document
              </p>
              <FreeCountsBar analysis={analysis} />
            </div>
          )}

          {/* Personal block — teaser when free, full block when premium */}
          {inFocus && selectedPersonal && !isPremium && (() => {
            // Build real, data-driven hints from the analysis — same data
            // that powers the premium checklist but degraded to category
            // statements (no figures, no specifics) for the free teaser.
            const role = selectedPersonal.detected_role || 'partie identifiée';
            const myAssetsCount = (analysis.assets || []).filter((a) => {
              if (!a) return false;
              if (a.beneficiary && partyMentioned(a.beneficiary, selectedParty)) return true;
              if (Array.isArray(a.beneficiaries)) {
                return a.beneficiaries.some((b) => partyMentioned(b?.name, selectedParty));
              }
              return false;
            }).length;
            const hasAmount = Boolean(
              selectedPersonal.financial_synthesis?.primary_amount
              || selectedPersonal.value_received
              || selectedPersonal.estimated_personal_value,
            );
            const hasFiscal = Boolean(
              selectedPersonal.taxes_due
              || selectedPersonal.financial_synthesis?.fiscal_status,
            );
            const hasAction = Boolean(
              selectedPersonal.immediate_action
              && !NO_ACTION_RE.test(selectedPersonal.immediate_action),
            );
            const dynamicHints = [
              `Vous avez été identifié comme «\u00A0${role}\u00A0» dans cet acte.`,
            ];
            if (myAssetsCount > 0) {
              dynamicHints.push(
                myAssetsCount === 1
                  ? 'Un bien vous concerne directement.'
                  : `${myAssetsCount} biens vous concernent directement.`,
              );
            }
            if (hasAmount) {
              dynamicHints.push('Une valeur patrimoniale personnelle a été identifiée.');
            }
            if (hasFiscal) {
              dynamicHints.push('Une situation fiscale a été analysée.');
            }
            if (hasAction) {
              dynamicHints.push('Une démarche reste à effectuer après cet acte.');
            }
            // Pad to 4 minimum
            while (dynamicHints.length < 4) {
              dynamicHints.push('Une information personnelle a été identifiée pour vous.');
            }
            return (
              <PremiumTeaserCard
                selectedParty={selectedParty}
                hints={dynamicHints}
                onUnlock={requestPremiumUnlock}
                onChange={() => setSelectedParty(null)}
              />
            );
          })()}
          {inFocus && selectedPersonal && isPremium && (() => {
            const heroSentence =
              selectedPersonal.what_received ||
              selectedPersonal.personal_summary ||
              null;
            // Priority MUST match the PDF generator so the web and the
            // exported dossier always display the same headline figure:
            //   1. financial_synthesis.primary_amount (canonical, PDF source)
            //   2. value_received                     (fiscal fallback)
            //   3. estimated_personal_value           (estimation fallback)
            const amountSource =
              selectedPersonal.financial_synthesis?.primary_amount ||
              selectedPersonal.value_received ||
              selectedPersonal.estimated_personal_value ||
              null;
            const heroAmount = extractAmount(amountSource);
            const heroAmountNum = parseAmountToNumber(amountSource);
            // Anti-anomaly: suppress the giant hero amount when the
            // parsed value is suspiciously tiny compared to the dossier's
            // total declared value (e.g. when extraction silently picked
            // only the decimal slice "20 €" of "330 725,20 €"). The web
            // and the PDF must show the same headline.
            const referenceTotal =
              analysis?.estate_summary?.gross_estate_value ||
              analysis?.estate_summary?.total_declared_value ||
              null;
            const heroAmountIsAnomalous = isAnomalouslyLow(
              amountSource,
              referenceTotal,
            );
            const showAmountCard =
              Boolean(heroAmount) &&
              isMeaningfulAmount(amountSource) &&
              !heroAmountIsAnomalous;
            const status = deriveActionStatus(
              selectedPersonal.immediate_action,
              selectedPersonal.action_status,
            );
            const statusTone =
              status.tone === 'urgent'
                ? 'bg-red-50 text-red-800 border-red-200'
                : status.tone === 'warn'
                ? 'bg-amber-50 text-amber-900 border-amber-200'
                : 'bg-[#F0F4EE] text-[#2D6A4F] border-[#2D6A4F]/25';

            return (
            <>
            {/* v10 — single-sentence personal headline (top of all personal cards) */}
            <PersonalOneLinerCard personal={selectedPersonal} />
            {/* v8 — fused headline block (March 2026 directive) */}
            <KeyTakeawayCard
              personal={selectedPersonal}
              analysis={analysis}
              selectedParty={selectedParty}
            />
            <PatrimonialImpactCard
              personal={selectedPersonal}
              analysis={analysis}
            />
            <FinancialSynthesisCard
              synth={selectedPersonal.financial_synthesis}
              partyName={selectedParty}
            />
            <TopPersonalQuestionsCard
              questions={selectedPersonal.top_personal_questions}
              selectedParty={selectedParty}
            />
            <Card className="bg-white rounded-2xl p-6 sm:p-8 border-2 border-[#1A2530] shadow-md" data-testid="personal-block-top">
              <div className="flex items-start gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-[#1A2530] flex items-center justify-center flex-shrink-0">
                  <UserCheck className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-['Work_Sans'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#2C363F] mb-1">
                    Lecture personnelle de {selectedParty}
                  </p>
                  <h2 className="font-['Cormorant_Garamond'] font-medium text-2xl sm:text-3xl text-[#1A2530] leading-tight">
                    Ce que vous recevez personnellement
                  </h2>
                  {selectedPersonal.legal_mechanism && (
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <span className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.18em] text-[#1A2530] bg-[#E3D5CA] px-2.5 py-1 rounded-full">
                        {selectedPersonal.legal_mechanism}
                      </span>
                      <ConfidenceBadge confidence={selectedPersonal.role_confidence} size="xs" />
                    </div>
                  )}
                </div>
              </div>

              {/* Large amount card — primary visual emphasis (when a fiscal
                  value is detected for this party). */}
              {showAmountCard && (
                <div
                  className="mb-5 p-6 sm:p-8 rounded-2xl bg-[#F9F7F3] border border-[#E2DBCF] text-center"
                  data-testid="personal-amount-card"
                >
                  <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#2C363F] opacity-80 mb-3">
                    Valeur patrimoniale identifiée dans cet acte
                  </p>
                  <p className="font-['Cormorant_Garamond'] font-semibold text-[#1A2530] leading-[1.05] text-[44px] sm:text-[60px] lg:text-[72px]">
                    {heroAmount}
                  </p>
                  {amountSource && amountSource !== heroAmount && (
                    <p className="font-['Work_Sans'] text-[12px] sm:text-[13px] text-[#2C363F] italic opacity-80 mt-3 max-w-xl mx-auto leading-snug">
                      {amountSource}
                    </p>
                  )}
                </div>
              )}

              {/* HERO — what the person RECEIVES (real right, not money) */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-5" data-testid="personal-hero">
                {/* Real right received — the giant element */}
                <div className="lg:col-span-3 p-6 sm:p-7 rounded-2xl bg-[#1A2530] text-white flex flex-col justify-center">
                  <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#E3D5CA] opacity-80 mb-2">
                    Ce que vous recevez réellement
                  </p>
                  <p
                    className="font-['Cormorant_Garamond'] font-semibold leading-[1.05] text-white text-[28px] sm:text-[36px] lg:text-[44px]"
                    data-testid="personal-share-label"
                  >
                    {selectedPersonal.share_label ||
                      selectedPersonal.what_received ||
                      'Droit non identifié dans le document'}
                  </p>
                  {selectedPersonal.what_received && selectedPersonal.share_label &&
                   selectedPersonal.what_received !== selectedPersonal.share_label && (
                    <p className="font-['Work_Sans'] text-[13px] sm:text-[14px] text-[#E3D5CA] opacity-90 mt-3 leading-snug">
                      {selectedPersonal.what_received}
                    </p>
                  )}
                  {selectedPersonal.value_received && (
                    <div className="mt-4 pt-4 border-t border-white/15">
                      <p className="font-['IBM_Plex_Mono'] text-[9px] uppercase tracking-[0.2em] text-[#E3D5CA] opacity-70 mb-1">
                        Information secondaire
                      </p>
                      <p
                        className="font-['Work_Sans'] text-[13px] sm:text-[14px] text-[#E3D5CA] leading-snug"
                        data-testid="personal-fiscal-value"
                      >
                        {selectedPersonal.value_received}
                      </p>
                    </div>
                  )}
                </div>

                {/* Status card */}
                <div
                  className={`lg:col-span-2 p-5 rounded-2xl border flex flex-col justify-center ${statusTone}`}
                  data-testid="personal-status"
                >
                  <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.22em] opacity-80 mb-2">
                    Statut
                  </p>
                  <p className="font-['Work_Sans'] font-semibold text-[16px] sm:text-[17px] leading-tight">
                    {status.label}
                  </p>
                  {status.actor && (
                    <p className="font-['Work_Sans'] text-[12px] sm:text-[13px] mt-2 leading-snug">
                      <span className="opacity-70">Qui agit · </span>
                      <span className="font-semibold">{status.actor}</span>
                    </p>
                  )}
                  {status.detail && status.tone !== 'calm' && (
                    <p className="font-['Work_Sans'] text-[12px] mt-2 leading-snug opacity-90">
                      {status.detail}
                    </p>
                  )}
                  {selectedPersonal.next_steps && (
                    <div className="mt-3 pt-3 border-t border-current/20 opacity-90">
                      <p className="font-['IBM_Plex_Mono'] text-[9px] uppercase tracking-[0.2em] opacity-70 mb-1">
                        Prochaines étapes
                      </p>
                      <p className="font-['Work_Sans'] text-[12px] leading-snug">
                        {selectedPersonal.next_steps}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Mes biens — filtered from analysis.assets */}
              {(() => {
                const myAssets = (analysis.assets || []).filter(assetConcernsMe);
                if (myAssets.length === 0) return null;
                return (
                  <div className="mb-5" data-testid="personal-mes-biens">
                    <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.22em] text-[#2C363F] mb-3">
                      Mes biens
                    </p>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {myAssets.map((a, idx) => {
                        const myShare =
                          (a.beneficiaries || []).find((b) =>
                            partyMentioned(b.name, selectedParty)
                          ) || null;
                        const right =
                          myShare?.right || a.rights_transferred || null;
                        return (
                          <div
                            key={idx}
                            className="p-4 rounded-xl border border-[#E2DBCF] bg-[#F9F7F3]/60"
                            data-testid={`personal-asset-${idx}`}
                          >
                            <p className="font-['Work_Sans'] font-semibold text-[14px] text-[#1A2530] leading-snug">
                              {a.name}
                            </p>
                            {a.address && (
                              <p className="font-['Work_Sans'] text-[12px] text-[#2C363F] leading-snug mt-0.5">
                                {a.address}
                              </p>
                            )}
                            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                              {right && (
                                <span className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.14em] text-[#1A2530] bg-[#E3D5CA]/60 px-2 py-0.5 rounded-full">
                                  {right}
                                </span>
                              )}
                              {myShare?.share && (
                                <span className="font-['IBM_Plex_Mono'] text-[10px] text-[#2C363F] bg-white border border-[#E2DBCF] px-2 py-0.5 rounded-full">
                                  {myShare.share}
                                </span>
                              )}
                              {a.value && (
                                <span className="font-['Work_Sans'] text-[12px] text-[#1A2530]">
                                  {a.value}
                                  {a.value_context ? ` (${a.value_context})` : ''}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* "Ce que cela change pour vous" — always shown when in
                  premium personal view. Translates legal data into concrete,
                  understandable consequences. Falls back to a generated list
                  if the LLM didn't return `consequences_plain`. */}
              {(() => {
                const explicit = has(selectedPersonal.consequences_plain)
                  ? selectedPersonal.consequences_plain
                  : null;

                let fallback = [];
                if (!explicit) {
                  const myAssets = (analysis.assets || []).filter(assetConcernsMe);
                  if (selectedPersonal.legal_mechanism_explanation) {
                    fallback.push(selectedPersonal.legal_mechanism_explanation);
                  } else if (selectedPersonal.legal_mechanism) {
                    fallback.push(
                      `Le document vous confère un droit de type « ${selectedPersonal.legal_mechanism} ». Votre notaire pourra en préciser les modalités exactes.`
                    );
                  }
                  if (myAssets.length === 1) {
                    fallback.push(
                      `Un bien identifié dans l'acte vous concerne directement. Sa désignation complète figure ci-dessus.`
                    );
                  } else if (myAssets.length > 1) {
                    fallback.push(
                      `${myAssets.length} biens identifiés dans l'acte vous concernent directement.`
                    );
                  }
                  if (selectedPersonal.taxes_due) {
                    fallback.push(
                      `Une situation fiscale a été identifiée : ${selectedPersonal.taxes_due}`
                    );
                  }
                  if (selectedPersonal.next_steps) {
                    fallback.push(
                      `Prochaine étape pour vous : ${selectedPersonal.next_steps}`
                    );
                  }
                  if (fallback.length === 0) {
                    fallback.push(
                      `Hérita a confirmé votre rôle dans l'acte. Les conséquences détaillées pourront être précisées avec votre notaire.`
                    );
                  }
                }

                const items = explicit || fallback;
                return (
                  <div
                    className="mb-5 p-5 rounded-xl bg-[#F0F4EE] border border-[#2D6A4F]/20"
                    data-testid="consequences-plain"
                  >
                    <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.22em] text-[#2D6A4F] mb-3">
                      Ce que cela change pour vous
                    </p>
                    <ul className="space-y-2">
                      {items.map((c, idx) => (
                        <li key={idx} className="flex items-start gap-2.5">
                          <CircleCheck className="h-4 w-4 text-[#2D6A4F] mt-0.5 flex-shrink-0" />
                          <span className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#1A2530] leading-snug">
                            {c}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()}

              {/* Détail du calcul fiscal */}
              {has(selectedPersonal.calculation_breakdown) && (
                <details className="mb-5 group" data-testid="calculation-breakdown">
                  <summary className="cursor-pointer font-['Work_Sans'] text-[12px] uppercase tracking-[0.18em] text-[#2C363F] hover:text-[#1A2530] inline-flex items-center gap-1.5 select-none">
                    <span>Détail du calcul fiscal</span>
                    <ArrowRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                  </summary>
                  <div className="mt-3 p-4 rounded-xl bg-[#F9F7F3] border border-[#E2DBCF]/70">
                    <table className="w-full text-[13px]">
                      <tbody>
                        {selectedPersonal.calculation_breakdown.map((step, idx) => (
                          <tr key={idx} className="border-b border-[#E2DBCF]/40 last:border-b-0">
                            <td className="py-2 font-['Work_Sans'] text-[#2C363F] pr-4">
                              {step.label}
                            </td>
                            <td className="py-2 font-['IBM_Plex_Mono'] text-right text-[#1A2530]">
                              {step.value}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="font-['Work_Sans'] text-[11px] text-[#2C363F] italic mt-3 leading-relaxed">
                      Décomposition reconstituée depuis le document. Les valeurs fiscales définitives restent à valider avec votre notaire.
                    </p>
                  </div>
                </details>
              )}

              {/* "Donc concrètement" key_takeaways */}
              {has(selectedPersonal.key_takeaways) && (
                <div className="mb-5 p-5 rounded-xl bg-[#F9F7F3] border border-[#E2DBCF]" data-testid="key-takeaways">
                  <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.22em] text-[#2C363F] opacity-80 mb-3">
                    Donc concrètement
                  </p>
                  <ul className="space-y-2">
                    {selectedPersonal.key_takeaways.map((t, idx) => (
                      <li key={idx} className="flex items-start gap-2.5">
                        <span className="font-['IBM_Plex_Mono'] text-[#1A2530] text-sm flex-shrink-0 mt-0.5 opacity-70">
                          —
                        </span>
                        <span className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#1A2530] leading-snug">
                          {t}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Q/A residual context (non-duplicated with hero) */}
              {(selectedPersonal.why_concerned || selectedPersonal.taxes_due) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5" data-testid="personal-qa">
                  {selectedPersonal.why_concerned && (
                    <div className="p-4 rounded-xl bg-[#F9F7F3] border border-[#E2DBCF]/70" data-testid="personal-qa-why">
                      <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] mb-1.5">
                        Pourquoi vous êtes concerné
                      </p>
                      <p className="font-['Work_Sans'] text-[14px] text-[#1A2530] leading-snug">
                        {selectedPersonal.why_concerned}
                      </p>
                    </div>
                  )}
                  {selectedPersonal.taxes_due && (
                    <div className="p-4 rounded-xl bg-[#F9F7F3] border border-[#E2DBCF]/70" data-testid="personal-qa-taxes">
                      <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] mb-1.5">
                        Droits de succession
                      </p>
                      <p className="font-['Work_Sans'] text-[14px] text-[#1A2530] leading-snug">
                        {selectedPersonal.taxes_due}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Aller plus loin */}
              {(selectedPersonal.legal_mechanism_explanation || selectedPersonal.personal_summary) && (
                <details className="group">
                  <summary className="cursor-pointer font-['Work_Sans'] text-[12px] uppercase tracking-[0.18em] text-[#2C363F] hover:text-[#1A2530] inline-flex items-center gap-1.5 select-none">
                    <span>Aller plus loin dans l'explication</span>
                    <ArrowRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                  </summary>
                  <div className="mt-3 space-y-3">
                    {selectedPersonal.legal_mechanism_explanation && (
                      <div className="p-4 bg-[#F9F7F3] rounded-xl border border-[#E2DBCF]/70">
                        <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] mb-1.5">
                          Mécanisme juridique
                        </p>
                        <p className="font-['Work_Sans'] text-[13px] text-[#1A2530] leading-relaxed italic">
                          {selectedPersonal.legal_mechanism_explanation}
                        </p>
                      </div>
                    )}
                    {selectedPersonal.personal_summary && (
                      <p className="font-['Work_Sans'] text-[14px] text-[#1A2530] leading-relaxed">
                        {selectedPersonal.personal_summary}
                      </p>
                    )}
                  </div>
                </details>
              )}

              <p className="font-['Work_Sans'] text-[11px] text-[#2C363F] italic mt-5 leading-relaxed">
                Sous réserve d'interprétation juridique — les chiffres et la fiscalité restent à valider avec votre notaire.
              </p>
            </Card>
            </>
            );
          })()}
          {(analysis.act_synthesis || analysis.document_summary) && (
            <Card className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-[#E2DBCF]">
              <p className="font-['Work_Sans'] text-[11px] sm:text-xs uppercase tracking-widest text-[#2C363F] mb-3">
                En 30 secondes
              </p>
              <h2 className="font-['Cormorant_Garamond'] font-medium text-2xl sm:text-3xl text-[#1A2530] mb-4 sm:mb-5 leading-tight">
                Ce que cet acte fait, concrètement
              </h2>
              {analysis.act_synthesis && (
                <p
                  className="font-['Work_Sans'] text-[15px] sm:text-[17px] text-[#1A2530] leading-relaxed whitespace-pre-line"
                  data-testid="act-synthesis"
                >
                  {analysis.act_synthesis}
                </p>
              )}
              {analysis.document_summary && analysis.document_summary !== analysis.act_synthesis && (
                <div className="mt-5 pt-5 border-t border-[#E2DBCF]/70">
                  <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] mb-2 opacity-80">
                    Lecture générale
                  </p>
                  <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#2C363F] leading-relaxed whitespace-pre-line">
                    {analysis.document_summary}
                  </p>
                </div>
              )}
            </Card>
          )}

          {/* ===== PREMIUM CTA CARD — only when the personal teaser is NOT
                shown (single-party docs, or no party selected yet). When a
                party is picked, the teaser above becomes the primary
                conversion surface. ===== */}
          {!isPremium && !(inFocus && selectedPersonal) && (
            <PremiumCTACard onUnlock={requestPremiumUnlock} />
          )}

          {/* ===== PREMIUM CONTENT BLOCK — blurred & locked in free mode ===== */}
          <LockedSection
            locked={!isPremium}
            innerClassName="space-y-8"
            focusReorder={inFocus && isPremium}
          >
          {/* Premium CTA banner — only visible when premium is unlocked */}
          {isPremium && (
            <div
              className="flex flex-wrap items-center justify-between gap-3 p-4 sm:p-5 rounded-2xl bg-[#1A2530] text-white"
              style={inFocus && isPremium ? { order: 0 } : undefined}
              data-testid="premium-unlocked-banner"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-[#E3D5CA] flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="h-4 w-4 text-[#1A2530]" />
                </div>
                <div className="min-w-0">
                  <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#E3D5CA] opacity-80">
                    Votre dossier Hérita est prêt
                  </p>
                  <p className="font-['Work_Sans'] text-[14px] font-semibold leading-tight">
                    Votre lecture complète est disponible
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleDownloadDossier}
                className="font-['Work_Sans'] text-[13px] font-semibold bg-[#E3D5CA] hover:bg-white text-[#1A2530] rounded-full px-4 py-2 inline-flex items-center gap-2 transition-colors"
                data-testid="download-dossier-pdf"
              >
                <FileText className="h-4 w-4" />
                Ouvrir le Dossier Hérita
              </button>
            </div>
          )}

          {/* Qui donne quoi à qui ? */}
          {has(analysis.transfers) && (
            <Card
              className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-[#E2DBCF]"
              style={inFocus && isPremium ? { order: 10 } : undefined}
              data-testid="transfers-section"
            >
              <SectionHeader
                eyebrow="Au cœur du document"
                title="Qui donne quoi à qui ?"
                description="Les relations détectées dans votre document, telles qu'elles semblent y figurer. Chaque ligne renvoie à sa page source."
              />
              <div className="space-y-3">
                {analysis.transfers.map((t, idx) => {
                  const mine = inFocus && transferConcernsMe(t);
                  const dimmed = inFocus && !mine && !isPremium;
                  return (
                  <Dimmable key={idx} dimmed={dimmed}>
                  <div
                    className={`p-4 sm:p-5 rounded-xl border bg-[#F9F7F3]/60 ${
                      mine ? 'border-[#1A2530] ring-2 ring-[#1A2530]/15 bg-white' : 'border-[#E2DBCF]'
                    }`}
                    data-testid={`transfer-${idx}`}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-5 items-start">
                      {/* Donor */}
                      <div className="sm:col-span-3">
                        <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] mb-1">
                          Donateur(s)
                        </p>
                        <p className="font-['Work_Sans'] font-semibold text-[14px] text-[#1A2530] leading-snug">
                          {t.donor || NOT_IDENTIFIED}
                        </p>
                      </div>

                      {/* Arrow */}
                      <div className="hidden sm:flex sm:col-span-1 justify-center pt-5">
                        <ArrowRight className="h-4 w-4 text-[#2C363F] opacity-70" />
                      </div>

                      {/* Recipient */}
                      <div className="sm:col-span-3">
                        <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] mb-1">
                          Bénéficiaire
                        </p>
                        <p className="font-['Work_Sans'] font-semibold text-[14px] text-[#1A2530] leading-snug">
                          {t.recipient || NOT_IDENTIFIED}
                        </p>
                      </div>

                      {/* Asset + Right */}
                      <div className="sm:col-span-5">
                        <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] mb-1">
                          Bien & droit transmis
                        </p>
                        <p className="font-['Work_Sans'] text-[14px] text-[#1A2530] leading-snug">
                          {t.asset || NOT_IDENTIFIED}
                        </p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                          {t.right && (
                            <span className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.14em] text-[#1A2530] bg-[#E3D5CA]/60 px-2 py-0.5 rounded-full">
                              {t.right}
                            </span>
                          )}
                          {t.share && (
                            <span className="font-['IBM_Plex_Mono'] text-[10px] text-[#2C363F] bg-white border border-[#E2DBCF] px-2 py-0.5 rounded-full">
                              {t.share}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {(t.note || t.source_page || t.confidence || t.relationship_uncertainty) && (
                      <div className="mt-3 pt-3 border-t border-[#E2DBCF]/70">
                        <div className="flex flex-wrap items-center gap-2">
                          {t.note && (
                            <p className="font-['Work_Sans'] text-[12px] text-[#2C363F] italic mr-2 flex-1 min-w-0 leading-snug">
                              {t.note}
                            </p>
                          )}
                          <ConfidenceBadge confidence={t.confidence} rationale={t.confidence_rationale} size="xs" />
                          <SourceTag page={t.source_page} />
                        </div>
                        {t.relationship_uncertainty && (
                          <div className="mt-2 flex items-start gap-2 p-2.5 rounded-lg bg-[#FBF6E6] border border-[#E9C46A]/40" data-testid={`transfer-uncertainty-${idx}`}>
                            <Info className="h-3.5 w-3.5 text-[#8B6A14] mt-0.5 flex-shrink-0" />
                            <p className="font-['Work_Sans'] text-[12px] text-[#8B6A14] leading-snug">
                              <span className="font-semibold">Lien à confirmer — </span>
                              {t.relationship_uncertainty}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  </Dimmable>
                  );
                })}
              </div>
              <p className="font-['Work_Sans'] text-[11px] text-[#2C363F] italic mt-4 leading-relaxed">
                Sous réserve d'interprétation juridique — votre notaire reste votre référence.
              </p>
            </Card>
          )}

          {/* Estate Summary */}
          {analysis.estate_summary && (
            <Card
              className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-[#E2DBCF]"
              style={inFocus && isPremium ? { order: 11 } : undefined}
              data-testid="estate-summary-section"
            >
              <SectionHeader
                eyebrow="Vue patrimoniale"
                title="Synthèse patrimoniale"
                description="Récapitulatif des biens et de leur répartition tels qu'ils ressortent du document."
              />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                <div className="p-4 rounded-xl bg-[#F9F7F3] border border-[#E2DBCF]/60">
                  <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] mb-1">
                    Valeur totale identifiée
                  </p>
                  <p className="font-['Cormorant_Garamond'] text-2xl text-[#1A2530] font-semibold leading-tight">
                    {analysis.estate_summary.total_declared_value || NOT_IDENTIFIED}
                  </p>
                  {analysis.estate_summary.total_value_context && (
                    <p className="font-['Work_Sans'] text-[11px] text-[#2C363F] mt-1 leading-snug italic">
                      {analysis.estate_summary.total_value_context}
                    </p>
                  )}
                </div>
                <div className="p-4 rounded-xl bg-[#F9F7F3] border border-[#E2DBCF]/60">
                  <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] mb-1">
                    Biens identifiés
                  </p>
                  <p className="font-['Cormorant_Garamond'] text-2xl text-[#1A2530] font-semibold leading-tight">
                    {analysis.estate_summary.assets_count ?? (analysis.assets || []).length}
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-[#F9F7F3] border border-[#E2DBCF]/60">
                  <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] mb-1">
                    Bénéficiaires identifiés
                  </p>
                  <p className="font-['Cormorant_Garamond'] text-2xl text-[#1A2530] font-semibold leading-tight">
                    {(analysis.estate_summary.heirs_shares || []).length || '—'}
                  </p>
                </div>
              </div>

              {has(analysis.estate_summary.heirs_shares) && (
                <div className="space-y-2">
                  {analysis.estate_summary.heirs_shares.map((h, idx) => {
                    const mine = inFocus && partyMentioned(h.name, selectedParty);
                    const dimmed = inFocus && !mine && !isPremium;
                    return (
                    <Dimmable key={idx} dimmed={dimmed}>
                    <div className={`p-4 rounded-xl border bg-white ${mine ? 'border-[#1A2530] ring-2 ring-[#1A2530]/15' : 'border-[#E2DBCF]'}`} data-testid={`heir-share-${idx}`}>
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-['Work_Sans'] font-semibold text-[14px] text-[#1A2530] leading-snug">
                            {h.name}
                          </p>
                          {h.share_description && (
                            <p className="font-['Work_Sans'] text-[12px] text-[#2C363F] mt-0.5">
                              {h.share_description}
                            </p>
                          )}
                          {h.rights_received && (
                            <span className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.14em] text-[#1A2530] bg-[#E3D5CA]/60 px-2 py-0.5 rounded-full inline-block mt-1.5">
                              {h.rights_received}
                            </span>
                          )}
                        </div>
                        {h.value_summary && (
                          <p className="font-['Cormorant_Garamond'] text-lg text-[#1A2530] font-semibold whitespace-nowrap">
                            {h.value_summary}
                          </p>
                        )}
                      </div>
                      {has(h.assets_received) && (
                        <ul className="mt-2 space-y-0.5">
                          {h.assets_received.map((name, j) => (
                            <li key={j} className="font-['Work_Sans'] text-[12px] text-[#2C363F]">
                              · {name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    </Dimmable>
                    );
                  })}
                </div>
              )}

              {analysis.estate_summary.usufruit_nue_note && (
                <div className="mt-4 p-3 rounded-lg bg-[#FBF6E6] border border-[#E9C46A]/40 flex items-start gap-2">
                  <Info className="h-4 w-4 text-[#8B6A14] mt-0.5 flex-shrink-0" />
                  <p className="font-['Work_Sans'] text-[12px] text-[#8B6A14] leading-relaxed">
                    {analysis.estate_summary.usufruit_nue_note}
                  </p>
                </div>
              )}
            </Card>
          )}

          {/* Lots et biens identifiés */}
          {has(analysis.assets) && (
            <Card
              className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-[#E2DBCF]"
              style={inFocus && isPremium ? { order: 4 } : undefined}
              data-testid="assets-section"
            >
              <SectionHeader
                eyebrow="Patrimoine concerné"
                title={inFocus ? 'Biens qui vous concernent' : 'Lots et biens identifiés'}
                description={
                  inFocus
                    ? 'Biens en lien direct avec votre situation. Les autres lots restent visibles, mais grisés, afin que vous puissiez à tout moment voir le contexte global.'
                    : "Chaque montant est rattaché à un bien précis, à son bénéficiaire et au droit transmis. Aucun chiffre n'est isolé."
                }
              />
              <div className="space-y-4">
                {analysis.assets.map((a, idx) => {
                  const mine = inFocus && assetConcernsMe(a);
                  const dimmed = inFocus && !mine && !isPremium;
                  return (
                  <Dimmable key={idx} dimmed={dimmed}>
                  <div
                    className={`p-5 rounded-xl border bg-[#F9F7F3]/40 ${
                      mine ? 'border-[#1A2530] ring-2 ring-[#1A2530]/15 bg-white' : 'border-[#E2DBCF]'
                    }`}
                    data-testid={`asset-${idx}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-['Work_Sans'] font-semibold text-[15px] text-[#1A2530] leading-snug">
                          {a.name}
                        </p>
                        {a.address && (
                          <p className="font-['Work_Sans'] text-[13px] text-[#2C363F] leading-snug mt-0.5">
                            {a.address}
                          </p>
                        )}
                      </div>
                      {a.value && (
                        <div className="text-right">
                          <p className="font-['Cormorant_Garamond'] text-2xl text-[#1A2530] font-semibold leading-none">
                            {a.value}
                          </p>
                          {a.value_context && (
                            <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-wider text-[#2C363F] mt-1">
                              {a.value_context}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-[#E2DBCF]/70">
                      <div className="sm:col-span-2">
                        <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] mb-1.5">
                          {has(a.beneficiaries) && a.beneficiaries.length > 1 ? 'Bénéficiaires & droits' : 'Bénéficiaire & droit'}
                        </p>
                        {has(a.beneficiaries) ? (
                          <ul className="space-y-1.5">
                            {a.beneficiaries.map((b, j) => (
                              <li
                                key={j}
                                className="flex flex-wrap items-baseline gap-x-2 gap-y-1"
                                data-testid={`asset-${idx}-beneficiary-${j}`}
                              >
                                <span className="font-['Work_Sans'] font-semibold text-[14px] text-[#1A2530]">
                                  {b.name}
                                </span>
                                {b.right && (
                                  <span className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.14em] text-[#1A2530] bg-[#E3D5CA]/60 px-2 py-0.5 rounded-full">
                                    {b.right}
                                  </span>
                                )}
                                {b.share && (
                                  <span className="font-['IBM_Plex_Mono'] text-[10px] text-[#2C363F] bg-white border border-[#E2DBCF] px-2 py-0.5 rounded-full">
                                    {b.share}
                                  </span>
                                )}
                                <ConfidenceBadge
                                  confidence={b.confidence}
                                  rationale={b.confidence_rationale}
                                  size="xs"
                                />
                              </li>
                            ))}
                          </ul>
                        ) : (
                          (() => {
                            const isSuccession = (analysis.document_category || '')
                              .toLowerCase()
                              .includes('succession');
                            // v8.2 — for inheritance declarations, never display
                            // "non identifié dans le document" as a beneficiary
                            // line: the form simply doesn't split the asset.
                            const fallback = isSuccession
                              ? 'La déclaration ne précise pas la répartition individuelle de ce bien entre les héritiers.'
                              : NOT_IDENTIFIED;
                            return (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <p
                                  className="font-['Work_Sans'] text-[13px] text-[#1A2530] sm:col-span-2"
                                  data-testid={`asset-${idx}-beneficiary-fallback`}
                                >
                                  {a.beneficiary || fallback}
                                </p>
                                {(a.rights_transferred || !a.beneficiary && !isSuccession) && (
                                  <p className="font-['Work_Sans'] text-[13px] text-[#1A2530]">
                                    {a.rights_transferred || NOT_IDENTIFIED}
                                  </p>
                                )}
                              </div>
                            );
                          })()
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <ConfidenceBadge
                        confidence={a.confidence}
                        rationale={a.confidence_rationale}
                        size="xs"
                      />
                      <SourceTag page={a.source_page} reference={a.source_reference} />
                    </div>
                    {a.relationship_uncertainty && (
                      <div className="mt-2 flex items-start gap-2 p-2.5 rounded-lg bg-[#FBF6E6] border border-[#E9C46A]/40" data-testid={`asset-uncertainty-${idx}`}>
                        <Info className="h-3.5 w-3.5 text-[#8B6A14] mt-0.5 flex-shrink-0" />
                        <p className="font-['Work_Sans'] text-[12px] text-[#8B6A14] leading-snug">
                          <span className="font-semibold">Rattachement à confirmer — </span>
                          {a.relationship_uncertainty}
                        </p>
                      </div>
                    )}
                  </div>
                  </Dimmable>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Ce qui vous concerne personnellement */}
          {has(analysis.personal_relevance) && (
            <Card className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-[#E2DBCF]" data-testid="personal-relevance-section">
              <SectionHeader
                eyebrow="Lecture personnalisée"
                title="Ce qui vous concerne personnellement"
                description="Si vous êtes l'une des parties nommées dans le document, voici ce qui ressort vous concernant. Sélectionnez la fiche qui correspond à votre situation."
              />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {analysis.personal_relevance.map((p, idx) => {
                  const isMe = inFocus && personalCardIsMine(p);
                  const dimmed = inFocus && !isMe && !isPremium;
                  return (
                  <Dimmable key={idx} dimmed={dimmed}>
                  <div
                    className={`p-5 rounded-xl border bg-[#F9F7F3]/60 transition-colors h-full flex flex-col ${
                      isMe
                        ? 'border-[#1A2530] ring-2 ring-[#1A2530]/15 bg-white'
                        : 'border-[#E2DBCF]'
                    }`}
                    data-testid={`personal-${idx}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-9 h-9 rounded-full bg-[#E3D5CA] flex items-center justify-center flex-shrink-0">
                        {isMe ? (
                          <UserCheck className="h-4 w-4 text-[#1A2530]" />
                        ) : (
                          <Users className="h-4 w-4 text-[#1A2530]" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-['Work_Sans'] font-semibold text-[15px] text-[#1A2530] leading-snug">
                          {p.party_name}
                        </p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          {p.detected_role && (
                            <span className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.14em] text-[#1A2530] bg-[#E3D5CA]/60 px-2 py-0.5 rounded-full">
                              {p.detected_role}
                            </span>
                          )}
                          <ConfidenceBadge confidence={p.role_confidence} size="xs" />
                        </div>
                      </div>
                    </div>
                    {has(p.items) && (
                      <ul className="space-y-2.5 pl-1 flex-1">
                        {p.items.map((it, j) => (
                          <li key={j} className="border-l-2 border-[#D3CCBE] pl-3">
                            <p className="font-['Work_Sans'] text-[13px] font-semibold text-[#1A2530] leading-snug">
                              {it.label}
                            </p>
                            <p className="font-['Work_Sans'] text-[13px] text-[#2C363F] leading-snug mt-0.5">
                              {it.detail}
                            </p>
                            {it.source_page && (
                              <div className="mt-1.5">
                                <SourceTag page={it.source_page} />
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-4 pt-3 border-t border-[#E2DBCF]/70 flex items-center justify-end">
                      {isMe ? (
                        <button
                          onClick={() => setSelectedParty(null)}
                          className="font-['Work_Sans'] text-[12px] text-[#1A2530] hover:text-[#2C363F] inline-flex items-center gap-1.5"
                          data-testid={`personal-deselect-${idx}`}
                        >
                          <UserCheck className="h-3.5 w-3.5" />
                          Vous êtes ici · Désélectionner
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setSelectedParty(p.party_name);
                            if (typeof window !== 'undefined') {
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                          }}
                          className="font-['Work_Sans'] text-[12px] text-[#2C363F] hover:text-[#1A2530] border border-[#E2DBCF] hover:border-[#1A2530] rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
                          data-testid={`personal-select-${idx}`}
                        >
                          Je suis cette personne
                          <ArrowRight className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  </Dimmable>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Identified Information */}
          <Card className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-[#E2DBCF]" data-testid="identified-info-section">
            <SectionHeader
              eyebrow="Lecture du document"
              title="Informations identifiées dans le document"
              description="Seules les informations effectivement trouvées dans votre fichier sont listées ici. Chaque élément précise sa confiance et sa page source quand elles sont connues."
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1 divide-y sm:divide-y-0 divide-[#E2DBCF]">
              <Field
                label="Nature de l'acte"
                value={analysis.document_category}
                confidence={analysis.document_category_confidence}
              />
              <Field
                label="Date du document"
                value={analysis.document_date}
                confidence={analysis.document_date_confidence}
                confidenceRationale={analysis.document_date_confidence_rationale}
                sourcePage={analysis.document_date_source_page}
              />
              <Field
                label="Référence du dossier"
                value={analysis.document_reference}
                confidence={analysis.document_reference_confidence}
                confidenceRationale={analysis.document_reference_confidence_rationale}
                sourcePage={analysis.document_reference_source_page}
              />
              <Field
                label="Notaire"
                value={analysis.notary_name}
                confidence={analysis.notary_name_confidence}
                confidenceRationale={analysis.notary_name_confidence_rationale}
                sourcePage={analysis.notary_name_source_page}
              />
              <Field
                label="Étude notariale"
                value={analysis.notary_office}
                confidence={analysis.notary_office_confidence}
                confidenceRationale={analysis.notary_office_confidence_rationale}
                sourcePage={analysis.notary_office_source_page}
              />
              <Field
                label="Adresse du bien"
                value={analysis.property_address}
                confidence={analysis.property_address_confidence}
                confidenceRationale={analysis.property_address_confidence_rationale}
                sourcePage={analysis.property_address_source_page}
              />
            </div>

            {has(analysis.parties) && (
              <div className="mt-8 pt-6 border-t border-[#E2DBCF]">
                <p className="font-['Work_Sans'] text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-[#2C363F] mb-3">
                  Parties identifiées
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {analysis.parties.map((party, idx) => (
                    <div key={idx} className="p-4 bg-[#F9F7F3] rounded-xl border border-[#E2DBCF]/60" data-testid={`party-${idx}`}>
                      <p className="font-['Work_Sans'] font-semibold text-[14px] text-[#1A2530] leading-snug">
                        {party.name}
                      </p>
                      {party.role && (
                        <p className="font-['Work_Sans'] text-[12px] text-[#2C363F] leading-snug mt-1">
                          {party.role}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        <ConfidenceBadge confidence={party.confidence} size="xs" />
                        <SourceTag page={party.source_page} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {has(analysis.key_dates) && (
              <div className="mt-8 pt-6 border-t border-[#E2DBCF]">
                <p className="font-['Work_Sans'] text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-[#2C363F] mb-3">
                  Dates identifiées
                </p>
                <div className="space-y-3">
                  {analysis.key_dates.map((d, idx) => (
                    <div key={idx} className="flex flex-col sm:flex-row sm:items-start sm:gap-6 p-3 bg-[#F9F7F3] rounded-xl">
                      <div className="sm:w-40 flex-shrink-0">
                        <p className="font-['IBM_Plex_Mono'] text-[13px] text-[#1A2530]">{d.date}</p>
                      </div>
                      <div className="min-w-0 flex-1 mt-1 sm:mt-0">
                        <p className="font-['Work_Sans'] font-semibold text-[14px] text-[#1A2530] leading-snug">
                          {d.label}
                        </p>
                        {d.description && (
                          <p className="font-['Work_Sans'] text-[12px] text-[#2C363F] leading-snug mt-1">
                            {d.description}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          <ConfidenceBadge confidence={d.confidence} size="xs" />
                          <SourceTag page={d.source_page} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Clarifying Questions */}
          {has(analysis.clarifying_questions) && (
            <Card className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-[#E2DBCF]" data-testid="clarifying-questions-section">
              <SectionHeader
                eyebrow="Ce que ce document éclaire"
                title="Questions que ce document permet d'éclairer"
                description="Quelques questions concrètes auxquelles la lecture du document apporte un début de réponse. Chaque réponse renvoie à sa source."
              />
              <div className="space-y-3">
                {analysis.clarifying_questions.map((q, idx) => (
                  <div
                    key={idx}
                    className="p-4 sm:p-5 rounded-xl border border-[#E2DBCF] bg-[#F9F7F3]/60"
                    data-testid={`clarifying-${idx}`}
                  >
                    <div className="flex items-start gap-3">
                      <HelpCircle className="h-4 w-4 text-[#1A2530] mt-1 flex-shrink-0 opacity-70" />
                      <div className="flex-1 min-w-0">
                        <p className="font-['Cormorant_Garamond'] text-[17px] sm:text-[19px] text-[#1A2530] leading-snug italic" style={{ fontWeight: 500 }}>
                          « {q.question} »
                        </p>
                        {q.answer && (
                          <p className="font-['Work_Sans'] text-[14px] text-[#1A2530] leading-relaxed mt-2">
                            {q.answer}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          <ConfidenceBadge confidence={q.confidence} rationale={q.confidence_rationale} size="xs" />
                          <SourceTag page={q.source_page} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Timeline */}
          {has(analysis.timeline) && (
            <Card className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-[#E2DBCF]" data-testid="timeline-section">
              <SectionHeader
                eyebrow="Repères dans le temps"
                title="Chronologie reconstituée"
                description="Événements identifiés dans le document, ordonnés du plus ancien au plus récent. Aucune date n'est inventée."
              />
              <ol className="relative border-l-2 border-[#E2DBCF] pl-6 space-y-5">
                {analysis.timeline.map((ev, idx) => {
                  const mine = inFocus && textConcernsMe(ev.label, ev.detail);
                  const dimmed = inFocus && !mine && !isPremium;
                  const status = ev.status || 'incertain';
                  const StatusIcon =
                    status === 'passé' ? CircleCheck : status === 'à venir' ? CircleDashed : CircleAlert;
                  const dotColor =
                    status === 'passé'
                      ? 'text-[#2D6A4F] bg-[#F0F4EE] border-[#2D6A4F]/30'
                      : status === 'à venir'
                      ? 'text-[#1A2530] bg-white border-[#E2DBCF]'
                      : 'text-[#8B6A14] bg-[#FBF6E6] border-[#E9C46A]/40';
                  return (
                    <Dimmable key={idx} dimmed={dimmed}>
                    <li className="relative" data-testid={`timeline-event-${idx}`}>
                      <span
                        className={`absolute -left-[34px] top-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${dotColor}`}
                      >
                        <StatusIcon className="h-3 w-3" />
                      </span>
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        {ev.date && (
                          <span className="font-['IBM_Plex_Mono'] text-[12px] text-[#1A2530]">
                            {ev.date}
                          </span>
                        )}
                        <span className="font-['Work_Sans'] font-semibold text-[14px] text-[#1A2530]">
                          {ev.label}
                        </span>
                        <span
                          className={`font-['IBM_Plex_Mono'] text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${
                            status === 'passé'
                              ? 'bg-[#F0F4EE] text-[#2D6A4F] border-[#2D6A4F]/25'
                              : status === 'à venir'
                              ? 'bg-[#E3D5CA]/40 text-[#1A2530] border-[#1A2530]/15'
                              : 'bg-[#FBF6E6] text-[#8B6A14] border-[#E9C46A]/40'
                          }`}
                        >
                          {status}
                        </span>
                      </div>
                      {ev.detail && (
                        <p className="font-['Work_Sans'] text-[12px] text-[#2C363F] leading-snug mt-1">
                          {ev.detail}
                        </p>
                      )}
                      <div className="mt-1">
                        <SourceTag page={ev.source_page} />
                      </div>
                    </li>
                    </Dimmable>
                  );
                })}
              </ol>
            </Card>
          )}

          {/* Next Actions */}
          {has(analysis.next_actions) && (
            <Card
              className="bg-[#1A2530] rounded-2xl p-6 sm:p-8 border border-[#1A2530] text-white"
              style={inFocus && isPremium ? { order: 2 } : undefined}
              data-testid="next-actions-section"
            >
              <p className="font-['Work_Sans'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#E3D5CA] mb-2 opacity-90">
                Pour avancer
              </p>
              <h2 className="font-['Cormorant_Garamond'] font-medium text-2xl sm:text-[28px] text-white mb-2 leading-tight">
                Que faire maintenant ?
              </h2>
              <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#E3D5CA] mb-6 leading-relaxed max-w-2xl opacity-90">
                {inFocus
                  ? `Actions filtrées pour votre rôle de «\u00A0${selectedPersonal?.detected_role || 'partie identifiée'}\u00A0». Les actions destinées aux autres rôles n'apparaissent pas ici.`
                  : "Chaque action est rattachée soit à un élément explicite du document, soit à une recommandation générale à confirmer avec votre notaire."}
              </p>
              <div className="space-y-3">
                {(() => {
                  const selectedRoleCanon = normalizeRole(selectedPersonal?.detected_role);
                  // STRICT role filtering when a party is selected:
                  // we HIDE (not dim) actions that target a different role.
                  const baseList = inFocus && selectedRoleCanon
                    ? analysis.next_actions.filter((na) => matchesSelectedRole(na, selectedRoleCanon))
                    : [...analysis.next_actions];
                  return baseList.sort((a, b) => {
                    const ar = normalizeRole(a.target_role) === selectedRoleCanon ? 0 : 1;
                    const br = normalizeRole(b.target_role) === selectedRoleCanon ? 0 : 1;
                    if (ar !== br) return ar - br;
                    const pOrder = { 'haute': 0, 'moyenne': 1, 'basse': 2 };
                    return (pOrder[a.priority] || 1) - (pOrder[b.priority] || 1);
                  });
                })().map((action, idx) => {
                  const priority = action.priority || 'moyenne';
                  const fromDoc = action.basis === 'document';
                  // In strict-filter mode all remaining actions are relevant
                  // to the selected role, so we never dim them.
                  const dimmed = false;
                  return (
                    <Dimmable key={idx} dimmed={dimmed}>
                    <div
                      className="p-4 sm:p-5 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10"
                      data-testid={`next-action-${idx}`}
                    >
                      <div className="flex items-start gap-4">
                        <span className="font-['IBM_Plex_Mono'] text-[#E3D5CA] font-medium text-sm mt-0.5 flex-shrink-0 opacity-80">
                          {String(idx + 1).padStart(2, '0')}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3 mb-1.5">
                            <p className="font-['Work_Sans'] font-semibold text-[15px] sm:text-base text-white leading-tight">
                              {action.title}
                            </p>
                            <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                              {action.classification && (
                                <span className={`font-['IBM_Plex_Mono'] text-[10px] tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${
                                  action.classification === 'Obligatoire' ? 'bg-[#E3D5CA] text-[#1A2530]' :
                                  action.classification === 'Recommandée' ? 'bg-white/15 text-[#E3D5CA] border border-white/25' :
                                  'bg-transparent text-[#E3D5CA] border border-white/20'
                                }`}>
                                  {action.classification}
                                </span>
                              )}
                              {action.confidence === 'élevée' && (
                                <span className="font-['IBM_Plex_Mono'] text-[10px] tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap bg-[#F0F4EE] text-[#2D6A4F] border border-[#2D6A4F]/40">
                                  Confiance élevée
                                </span>
                              )}
                              {action.confidence === 'moyenne' && (
                                <span className="font-['IBM_Plex_Mono'] text-[10px] tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap bg-white/10 text-[#E3D5CA] border border-white/20">
                                  Recommandation
                                </span>
                              )}
                              <span className={`font-['IBM_Plex_Mono'] text-[10px] tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${
                                priority === 'haute' ? 'bg-red-200/90 text-red-900' :
                                priority === 'moyenne' ? 'bg-amber-200/90 text-amber-900' :
                                'bg-white/15 text-[#E3D5CA]'
                              }`}>
                                {PRIORITY_LABEL[priority] || 'À PRÉVOIR'}
                              </span>
                            </div>
                          </div>
                          {action.detail && (
                            <p className="font-['Work_Sans'] text-[13px] sm:text-sm text-[#E3D5CA] leading-relaxed opacity-90">
                              {action.detail}
                            </p>
                          )}
                          {action.basis_explanation && (
                            <p className="font-['Work_Sans'] text-[12px] sm:text-[13px] text-[#E3D5CA] leading-snug mt-2 opacity-80 italic">
                              Pourquoi cette action ? {action.basis_explanation}
                            </p>
                          )}
                          {action.consequence_plain && (
                            <p className="font-['Work_Sans'] text-[12px] text-white leading-snug mt-2 px-2 py-1.5 bg-white/10 rounded-md italic">
                              → {action.consequence_plain}
                            </p>
                          )}
                          <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                            <span
                              className={`font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full ${
                                fromDoc
                                  ? 'bg-[#E3D5CA] text-[#1A2530]'
                                  : 'bg-white/10 text-[#E3D5CA] border border-white/20'
                              }`}
                            >
                              {fromDoc ? 'Basé sur le document' : 'Recommandation à confirmer'}
                            </span>
                            {action.source_page && (
                              <span className="font-['IBM_Plex_Mono'] text-[10px] tracking-[0.1em] uppercase text-[#E3D5CA] bg-white/10 border border-white/20 px-2 py-0.5 rounded-full">
                                Source · p. {action.source_page}
                              </span>
                            )}
                            {action.source_reference && (
                              <span
                                className="font-['Work_Sans'] text-[11px] text-[#E3D5CA] italic opacity-80 truncate max-w-full"
                                title={action.source_reference}
                              >
                                « {action.source_reference} »
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    </Dimmable>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Suggested complementary documents — presented as general
              suggestions, never as missing pieces. */}
          {has(analysis.recommended_documents) && (
            <Card
              className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-[#E2DBCF]"
              style={inFocus && isPremium ? { order: 20 } : undefined}
              data-testid="recommended-docs-section"
            >
              <SectionHeader
                eyebrow="Suggestions générales"
                title="Documents que vous pourriez utilement avoir sous la main"
                description="Liste informative, à titre indicatif. Ce ne sont PAS des pièces manquantes. Votre notaire vous précisera la liste exacte applicable à votre dossier."
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {analysis.recommended_documents.map((d, idx) => (
                  <div key={idx} className="p-4 bg-[#F9F7F3] rounded-xl border border-[#E2DBCF]/60" data-testid={`recommended-doc-${idx}`}>
                    <p className="font-['Work_Sans'] font-semibold text-[14px] text-[#1A2530] leading-snug">
                      {d.name}
                    </p>
                    {d.rationale && (
                      <p className="font-['Work_Sans'] text-[12px] text-[#2C363F] leading-snug mt-1">
                        {d.rationale}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <p className="font-['Work_Sans'] text-[11px] sm:text-[12px] text-[#2C363F] mt-5 italic leading-relaxed">
                Recommandations générales. Votre notaire confirmera la liste exhaustive applicable à votre dossier.
              </p>
            </Card>
          )}

          {/* Notary Questions — priority items first, role-targeted first */}
          {has(analysis.questions_for_notary) && (
            <Card
              className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-[#E2DBCF]"
              style={inFocus && isPremium ? { order: 3 } : undefined}
              data-testid="notary-questions-section"
            >
              <SectionHeader
                eyebrow="Préparer la rencontre"
                title={selectedParty
                  ? `Questions utiles à poser à votre notaire — ${selectedParty}`
                  : "Questions utiles à poser à votre notaire"}
                description={selectedParty
                  ? "Questions adaptées à votre rôle dans cet acte."
                  : "Quelques questions générées à partir du document, pour préparer sereinement votre prochain échange."}
              />
              <div className="space-y-3">
                {(() => {
                  const selectedRoleCanon = normalizeRole(selectedPersonal?.detected_role);
                  // STRICT role-based filtering: hide questions targeting
                  // OTHER roles. `tous` and unset target_role stay visible.
                  return [...analysis.questions_for_notary]
                    .filter((q) => matchesSelectedRole(q, selectedRoleCanon))
                    .sort((a, b) => {
                      const ap = normalizeRole(a?.target_role) === selectedRoleCanon ? 0 : 1;
                      const bp = normalizeRole(b?.target_role) === selectedRoleCanon ? 0 : 1;
                      if (ap !== bp) return ap - bp;
                      return (b?.is_priority ? 1 : 0) - (a?.is_priority ? 1 : 0);
                    });
                })().map((q, idx) => (
                  <div
                    key={idx}
                    className="p-4 sm:p-5 bg-[#F9F7F3] rounded-xl border border-[#E2DBCF]/60"
                    data-testid={`notary-question-${idx}`}
                  >
                    <div className="flex items-start gap-4">
                      <span className="font-['IBM_Plex_Mono'] text-[#1A2530] font-medium text-sm mt-0.5 flex-shrink-0 opacity-70">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          {q.category && (
                            <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.18em] text-[#2C363F] opacity-70">
                              {q.category}
                            </p>
                          )}
                          {q.is_priority && (
                            <span className="font-['IBM_Plex_Mono'] text-[9px] uppercase tracking-[0.18em] text-[#1A2530] bg-[#E3D5CA] px-2 py-0.5 rounded-full">
                              Prioritaire
                            </span>
                          )}
                          {q.target_role && (
                            <span className="font-['IBM_Plex_Mono'] text-[9px] uppercase tracking-[0.18em] text-[#1A2530] bg-white border border-[#E2DBCF] px-2 py-0.5 rounded-full">
                              {q.target_role}
                            </span>
                          )}
                          {q.confidence === 'élevée' && (
                            <span className="font-['IBM_Plex_Mono'] text-[9px] uppercase tracking-[0.18em] text-[#2D6A4F] bg-[#F0F4EE] border border-[#2D6A4F]/25 px-2 py-0.5 rounded-full">
                              Confiance élevée
                            </span>
                          )}
                          {q.confidence === 'moyenne' && (
                            <span className="font-['IBM_Plex_Mono'] text-[9px] uppercase tracking-[0.18em] text-[#2C363F] bg-white border border-[#E2DBCF] px-2 py-0.5 rounded-full">
                              Recommandation générale
                            </span>
                          )}
                        </div>
                        <p
                          className="font-['Cormorant_Garamond'] text-[17px] sm:text-[19px] text-[#1A2530] leading-snug italic"
                          style={{ fontWeight: 500 }}
                        >
                          « {q.question} »
                        </p>
                        {q.rationale && (
                          <p className="font-['Work_Sans'] text-[12px] sm:text-[13px] text-[#2C363F] leading-snug mt-2 opacity-90">
                            {q.rationale}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Uncertainties */}
          {(has(analysis.structured_uncertainties) || has(analysis.uncertainties)) && (
            <Card
              className="bg-[#FBF6E6] rounded-2xl p-6 sm:p-8 border border-[#E9C46A]/40"
              style={inFocus && isPremium ? { order: 5 } : undefined}
              data-testid="uncertainties-section"
            >
              <SectionHeader
                eyebrow="Honnêteté de l'analyse"
                title="Points qui mériteraient confirmation"
                description="Éléments où la lecture du document reste incertaine. Pour chaque point, nous indiquons la conséquence concrète possible si l'interprétation actuelle s'avérait incorrecte."
              />
              {has(analysis.structured_uncertainties) ? (
                <ul className="space-y-3">
                  {analysis.structured_uncertainties.map((u, idx) => {
                    const sev = u.severity || 'moyenne';
                    const sevColor =
                      sev === 'haute'
                        ? 'text-red-700'
                        : sev === 'faible'
                        ? 'text-[#2D6A4F]'
                        : 'text-[#8B6A14]';
                    return (
                      <li
                        key={idx}
                        className="p-4 rounded-xl bg-white border border-[#E9C46A]/40"
                        data-testid={`uncertainty-${idx}`}
                      >
                        <div className="flex items-start gap-3">
                          <Info className={`h-4 w-4 mt-0.5 flex-shrink-0 ${sevColor}`} />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-baseline gap-2 mb-1">
                              <p className="font-['Work_Sans'] font-semibold text-[14px] text-[#1A2530] leading-snug">
                                {u.label}
                              </p>
                              <span className={`font-['IBM_Plex_Mono'] text-[9px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${
                                sev === 'haute' ? 'bg-red-50 border-red-200 text-red-700' :
                                sev === 'faible' ? 'bg-[#F0F4EE] border-[#2D6A4F]/30 text-[#2D6A4F]' :
                                'bg-[#FBF6E6] border-[#E9C46A]/40 text-[#8B6A14]'
                              }`}>
                                {sev === 'haute' ? 'impact fort' : sev === 'faible' ? 'impact mineur' : 'à clarifier'}
                              </span>
                            </div>
                            {u.consequence && (
                              <p className="font-['Work_Sans'] text-[13px] text-[#2C363F] leading-relaxed italic">
                                → {u.consequence}
                              </p>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <ul className="space-y-3">
                  {analysis.uncertainties.map((u, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3 font-['Work_Sans'] text-[14px] text-[#2C363F] leading-relaxed"
                    >
                      <Info className="h-4 w-4 text-[#8B6A14] mt-0.5 flex-shrink-0" />
                      <span>{u}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}

          {/* Quick Actions */}
          {(has(analysis.glossary) || has(analysis.document_extracts)) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {has(analysis.glossary) && (
                <Button
                  data-testid={DASHBOARD.glossaryButton}
                  onClick={() => setGlossaryOpen(true)}
                  className="bg-[#E3D5CA] hover:bg-[#D3CCBE] text-[#1A2530] py-6 rounded-xl font-['Work_Sans'] font-semibold text-base justify-start h-auto"
                >
                  <Book className="mr-3 h-5 w-5" />
                  <div className="text-left">
                    <div>Consulter le glossaire</div>
                    <div className="text-sm font-normal opacity-75 mt-1">
                      {analysis.glossary.length} termes expliqués
                    </div>
                  </div>
                </Button>
              )}
              {has(analysis.document_extracts) && (
                <Button
                  data-testid="extracts-button"
                  onClick={() => setExtractsOpen(true)}
                  className="bg-[#1A2530] hover:bg-[#2C363F] text-white py-6 rounded-xl font-['Work_Sans'] font-semibold text-base justify-start h-auto"
                >
                  <FileText className="mr-3 h-5 w-5" />
                  <div className="text-left">
                    <div>Voir les extraits du document</div>
                    <div className="text-sm font-normal opacity-75 mt-1">
                      {analysis.document_extracts.length} extrait(s) verbatim
                    </div>
                  </div>
                </Button>
              )}
            </div>
          )}

          {/* ===== END PREMIUM CONTENT BLOCK ===== */}
          </LockedSection>

          {/* Footer — humility */}
          <div className="pt-8 sm:pt-10 mt-2 border-t border-[#E2DBCF]">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <p className="font-['Work_Sans'] text-[12px] sm:text-[13px] text-[#2C363F] leading-relaxed max-w-3xl">
                Hérita vous aide à comprendre votre document. Cette lecture ne remplace pas l'avis de votre notaire,
                qui reste votre référence pour toute décision juridique ou patrimoniale.
              </p>
              <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] tracking-[0.15em] text-[#2C363F] uppercase opacity-75 whitespace-nowrap flex items-center gap-2">
                <ShieldCheck className="h-3.5 w-3.5" />
                Lecture assistée · Hérita
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Glossary Modal */}
      <Dialog open={glossaryOpen} onOpenChange={setGlossaryOpen}>
        <DialogContent
          data-testid={DASHBOARD.glossaryModal}
          className="max-w-3xl bg-white/98 backdrop-blur-xl border-[#E2DBCF] max-h-[85vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-3xl text-[#1A2530]">
              Glossaire
            </DialogTitle>
            <DialogDescription className="font-['Work_Sans'] text-base text-[#2C363F]">
              Termes juridiques apparaissant dans votre document.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-6">
            {(analysis.glossary || []).map((item, idx) => (
              <div key={idx} className="p-5 bg-[#F9F7F3] rounded-xl">
                <h4 className="font-['Work_Sans'] font-semibold text-lg text-[#1A2530] mb-3">
                  {item.term}
                </h4>
                <p className="font-['Work_Sans'] text-base text-[#2C363F] leading-relaxed">
                  {item.definition}
                </p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Extracts Modal */}
      <Dialog open={extractsOpen} onOpenChange={setExtractsOpen}>
        <DialogContent
          data-testid="extracts-modal"
          className="max-w-4xl bg-white/98 backdrop-blur-xl border-[#E2DBCF] max-h-[85vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-3xl text-[#1A2530]">
              Extraits du document
            </DialogTitle>
            <DialogDescription className="font-['Work_Sans'] text-base text-[#2C363F]">
              Passages identifiés tels qu'ils figurent dans le fichier que vous avez transmis.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-6">
            {(analysis.document_extracts || []).map((extract, idx) => (
              <div key={idx} className="p-5 bg-[#F9F7F3] rounded-xl border border-[#E2DBCF]">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <p className="font-['Work_Sans'] font-semibold text-sm text-[#1A2530]">
                    {extract.label}
                  </p>
                  <SourceTag page={extract.source_page} />
                </div>
                <p className="font-['IBM_Plex_Mono'] text-sm text-[#2C363F] leading-relaxed whitespace-pre-line">
                  {extract.content}
                </p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pre-checkout reassurance modal — shown immediately above the
          Stripe button. Six trust-building items with sober icons, the
          one-time price block, the reassurance sentence and the CTA that
          launches Stripe Checkout. */}
      <Dialog
        open={accessOfferOpen}
        onOpenChange={setAccessOfferOpen}
      >
        <DialogContent
          data-testid="access-offer-modal"
          className="max-w-lg bg-white border-[#E2DBCF] p-0 overflow-hidden"
        >
          <div className="p-7 sm:p-9">
            <DialogHeader className="space-y-3 text-left">
              <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#2C363F] opacity-80">
                Débloquer votre dossier
              </p>
              <DialogTitle className="font-['Cormorant_Garamond'] font-medium text-[26px] sm:text-[30px] text-[#1A2530] leading-[1.15]">
                Votre lecture personnalisée vous attend.
              </DialogTitle>
              <DialogDescription className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#2C363F] leading-relaxed">
                Avant de passer au paiement, voici exactement ce qui est inclus
                et nos engagements.
              </DialogDescription>
            </DialogHeader>

            {/* Reassurance grid — 2 columns, 6 sober icon items */}
            <ul
              className="mt-6 mb-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5"
              data-testid="checkout-reassurance"
            >
              {[
                { label: 'Paiement unique', Icon: CircleCheck },
                { label: 'Aucun abonnement', Icon: CircleCheck },
                { label: 'Vous ne repayerez jamais pour ce document', Icon: ShieldCheck },
                { label: 'Paiement 100\u00A0% sécurisé par Stripe', Icon: Lock },
                { label: 'PDF Hérita inclus', Icon: Download },
                { label: 'Documents originaux supprimés automatiquement', Icon: Trash2 },
              ].map(({ label, Icon }, idx) => (
                <li
                  key={idx}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-[#F9F7F3] border border-[#E2DBCF]"
                  data-testid={`reassurance-item-${idx}`}
                >
                  <Icon className="h-3.5 w-3.5 text-[#1A2530] flex-shrink-0" />
                  <span className="font-['Work_Sans'] text-[12.5px] sm:text-[13px] text-[#1A2530] leading-snug">
                    {label}
                  </span>
                </li>
              ))}
            </ul>

            <p
              className="font-['Work_Sans'] text-[12.5px] sm:text-[13px] italic text-[#2C363F] opacity-90 leading-relaxed mb-5 px-1"
              data-testid="checkout-reassurance-sentence"
            >
              Votre dossier reste accessible après votre achat et vous ne
              repayerez jamais pour ce document.
            </p>

            <div className="mb-6 p-4 rounded-xl bg-[#1A2530] text-white">
              <p className="font-['Cormorant_Garamond'] font-semibold text-[22px] sm:text-[24px] leading-tight">
                19&nbsp;€ <span className="font-['Work_Sans'] font-normal text-[13px] sm:text-[14px] text-[#E3D5CA] ml-1">— paiement unique</span>
              </p>
              <p className="font-['Work_Sans'] text-[12px] sm:text-[13px] text-[#E3D5CA] mt-1 leading-relaxed">
                Accès immédiat à votre Dossier Hérita personnalisé, à votre lecture
                par rôle, et à l'export PDF.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={confirmAccessOffer}
                disabled={unlocking}
                className="w-full font-['Work_Sans'] text-[14px] sm:text-[15px] font-semibold bg-[#1A2530] hover:bg-[#2C363F] text-white rounded-full px-6 py-3.5 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="access-offer-confirm"
              >
                <ShieldCheck className="h-4 w-4" />
                {unlocking ? 'Redirection vers Stripe…' : 'Payer en sécurité avec Stripe — 19\u00A0€'}
              </button>
              <button
                type="button"
                onClick={() => setAccessOfferOpen(false)}
                className="font-['Work_Sans'] text-[12px] text-[#2C363F] hover:text-[#1A2530] underline underline-offset-2 self-center"
                data-testid="access-offer-cancel"
              >
                Plus tard
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dossier Hérita — rendered as INLINE HTML using the existing data.
          No blob, no popup, no iframe — guaranteed to work regardless of
          ad-blockers, popup blockers or iframe sandbox restrictions.
          A standard <a> link offers the PDF download for those who want it. */}
      <Dialog
        open={Boolean(dossierUrl)}
        onOpenChange={(open) => { if (!open) handleCloseDossier(); }}
      >
        <DialogContent
          data-testid="dossier-pdf-modal"
          className="max-w-4xl w-[95vw] bg-[#F9F7F3] border-[#E2DBCF] p-0 overflow-hidden flex flex-col"
          style={{ height: '92vh' }}
        >
          <DialogHeader className="px-6 py-4 border-b border-[#E2DBCF] bg-white flex flex-row items-center justify-between gap-4 space-y-0 flex-shrink-0">
            <div className="min-w-0">
              <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#2C363F]">
                Dossier confidentiel
              </p>
              <DialogTitle className="font-['Cormorant_Garamond'] text-2xl text-[#1A2530] leading-tight">
                Dossier Hérita
              </DialogTitle>
              <DialogDescription className="font-['Work_Sans'] text-[12px] text-[#2C363F] leading-snug mt-0.5">
                Lecture personnalisée {selectedParty ? `de ${selectedParty}` : ''}
              </DialogDescription>
            </div>
            <a
              href={`${API}/analyses/${analysis.document_id}/dossier.pdf` + (selectedParty ? `?party=${encodeURIComponent(selectedParty)}` : '')}
              download="Dossier-Herita.pdf"
              target="_top"
              rel="noopener"
              className="font-['Work_Sans'] text-[13px] font-semibold bg-[#1A2530] hover:bg-[#2C363F] text-white rounded-full px-4 py-2 inline-flex items-center gap-2 transition-colors flex-shrink-0"
              data-testid="save-dossier-pdf"
            >
              <FileText className="h-4 w-4" />
              Télécharger en PDF
            </a>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 sm:px-10 py-8 sm:py-10">
            <DossierInline analysis={analysis} selectedParty={selectedParty} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
};

export default DashboardPageV2;
